import { BOTGO_VIDEO_25_CAPABILITIES, getBotgoVideo25CreditCost } from '../lib/botgo-video-25.js';
import { GPT_IMAGE_25_SIZES, GPT_IMAGE_25_QUALITIES, GPT_IMAGE_25_DEFAULT_CREDITS, GPT_IMAGE_25_VIP_CREDITS, GPT_IMAGE_25_VIP_INPUT_IMAGE_RATE, GPT_IMAGE_25_EXT_RATIOS } from './gpt-image-25.js';
import { NANO_BANANA_21_CAPABILITIES, NANO_BANANA_21_CREDITS } from './nano-banana-21.js';
import { WAN_VIDEO_CAPABILITIES, getWanVideoCreditCost } from '../lib/wan-video.js';
import { KLING_V4_CAPABILITIES, getKlingV4CreditCost } from './kling-v4.js';
import { KLING_V3_CREDIT_COSTS } from './kling-credit-costs.js';
import { getGeminiOmniCreditCost } from '../lib/gemini-omni-billing.js';
import { getSeedance20CreditCost } from '../lib/seedance20-billing.js';
import { getMiniMaxH3CreditCost } from '../lib/minimax-h3-billing.js';

const normalizeAlias = (value = '') =>
  String(value).toLowerCase().replace(/[^a-z0-9]/g, '');

const parseResolution = (value = '') => {
  if (typeof value !== 'string') {
    return { width: null, height: null, is4k: false };
  }

  const normalized = value.toLowerCase();
  if (normalized.includes('4k')) {
    return { width: null, height: null, is4k: true };
  }

  const match = normalized.match(/(\d{3,5})\s*x\s*(\d{3,5})/);
  if (!match) {
    return { width: null, height: null, is4k: false };
  }

  const width = Number(match[1]);
  const height = Number(match[2]);
  const maxDimension = Math.max(width || 0, height || 0);
  return {
    width,
    height,
    is4k: maxDimension >= 2160,
  };
};

const normalizeSize = (value = '') =>
  String(value).trim().toLowerCase().replace(/\s+/g, '');

const normalizeResolutionLabel = (value = '') =>
  String(value).trim().toLowerCase();

const normalizeAspectRatioLabel = (value = '') =>
  String(value).trim();

const normalizeOptionalText = (value = '') =>
  String(value || '').trim();

const formatProviderGroupedDisplayName = (modelName, providerGroup = 'Default') => {
  const group = String(providerGroup || '').trim();
  if (!group || group.toLowerCase() === 'default') return modelName;
  return `${modelName} ${group}`;
};

const ASPECT_RATIO_VALUES = {
  '1:1': 1,
  '1:4': 1 / 4,
  '1:8': 1 / 8,
  '2:3': 2 / 3,
  '3:2': 3 / 2,
  '3:4': 3 / 4,
  '4:1': 4,
  '4:3': 4 / 3,
  '4:5': 4 / 5,
  '5:4': 5 / 4,
  '8:1': 8,
  '9:16': 9 / 16,
  '16:9': 16 / 9,
  '21:9': 21 / 9,
};

const SORA2_SIZES = {
  '9:16': { '720p': '720x1280' },
  '16:9': { '720p': '1280x720' },
};

const SORA2_PRO_SIZES = {
  '9:16': { '720p': '720x1280', '1024p': '1024x1792', '1080p': '1080x1920' },
  '16:9': { '720p': '1280x720', '1024p': '1792x1024', '1080p': '1920x1080' },
};

const SEEDANCE_15_SIZES = {
  '16:9': { '480p': '864x496', '720p': '1280x720', '1080p': '1920x1080' },
  '4:3': { '480p': '752x560', '720p': '1112x834', '1080p': '1664x1248' },
  '1:1': { '480p': '640x640', '720p': '960x960', '1080p': '1440x1440' },
  '3:4': { '480p': '560x752', '720p': '834x1112', '1080p': '1248x1664' },
  '9:16': { '480p': '496x864', '720p': '720x1280', '1080p': '1080x1920' },
  '21:9': { '480p': '992x432', '720p': '1470x630', '1080p': '2206x946' },
};

const SEEDANCE_20_SIZES = {
  '16:9': { '480p': '864x496', '720p': '1280x720', '1080p': '1920x1080', '4K': '3840x2160' },
  '4:3': { '480p': '752x560', '720p': '1112x834', '1080p': '1664x1248', '4K': '3326x2494' },
  '1:1': { '480p': '640x640', '720p': '960x960', '1080p': '1440x1440', '4K': '2880x2880' },
  '3:4': { '480p': '560x752', '720p': '834x1112', '1080p': '1248x1664', '4K': '2494x3326' },
  '9:16': { '480p': '496x864', '720p': '720x1280', '1080p': '1080x1920', '4K': '2160x3840' },
  '21:9': { '480p': '992x432', '720p': '1470x630', '1080p': '2206x946', '4K': '4398x1886' },
  'adaptive': { '480p': 'adaptive_480p', '720p': 'adaptive_720p', '1080p': 'adaptive_1080p', '4K': 'adaptive_4K' },
};

const SEEDANCE_25_SIZES = {
  '16:9': { '480p': '854x480', '720p': '1280x720', '1080p': '1920x1080' },
  '4:3': { '480p': '752x560', '720p': '1112x834', '1080p': '1664x1248' },
  '1:1': { '480p': '640x640', '720p': '960x960', '1080p': '1440x1440' },
  '3:4': { '480p': '560x752', '720p': '834x1112', '1080p': '1248x1664' },
  '9:16': { '480p': '480x854', '720p': '720x1280', '1080p': '1080x1920' },
  '21:9': { '480p': '992x432', '720p': '1470x630', '1080p': '2206x946' },
  adaptive: { '480p': 'adaptive_480p', '720p': 'adaptive_720p', '1080p': 'adaptive_1080p' },
};

const SEEDANCE_20_FAST_VIP_SIZES = {
  '16:9': { '480p': '864x496', '720p': '1280x720' },
  '4:3': { '480p': '752x560', '720p': '1112x834' },
  '1:1': { '480p': '640x640', '720p': '960x960' },
  '3:4': { '480p': '560x752', '720p': '834x1112' },
  '9:16': { '480p': '496x864', '720p': '720x1280' },
  '21:9': { '480p': '992x432', '720p': '1470x630' },
  'adaptive': { '480p': 'adaptive_480p', '720p': 'adaptive_720p' },
};

const SEEDANCE_20_MINI_SIZES = SEEDANCE_20_FAST_VIP_SIZES;

const SEEDANCE_20_FAST_SPECIAL_SIZES = {
  '16:9': { '720p': '1280x720' },
  '4:3': { '720p': '1112x834' },
  '1:1': { '720p': '960x960' },
  '3:4': { '720p': '834x1112' },
  '9:16': { '720p': '720x1280' },
  '21:9': { '720p': '1470x630' },
};

const SEEDANCE_20_ASPECT_RATIOS = ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'];
const SEEDANCE_20_VIP_ASPECT_RATIOS = [...SEEDANCE_20_ASPECT_RATIOS, 'adaptive'];
const SEEDANCE_20_RESOLUTIONS = ['480p', '720p', '1080p', '4K'];
const SEEDANCE_20_FAST_VIP_RESOLUTIONS = ['480p', '720p'];
const SEEDANCE_20_MINI_RESOLUTIONS = ['480p', '720p'];
const SEEDANCE_20_FAST_SPECIAL_RESOLUTIONS = ['720p'];
const SEEDANCE_20_DURATIONS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
const SEEDANCE_25_DURATIONS = Array.from({ length: 27 }, (_, index) => index + 4);
const SEEDANCE_20_FAST_SPECIAL_DURATIONS = [15];

const SEEDANCE_SR_MODEL_KEYS = new Set([
  'seedance-2.5',
  'seedance-2.0',
  'seedance-2.0-fast',
  'seedance-2.0-mini',
]);

export const VSR_MODEL_RATES = Object.freeze({
  'botgo-vsr-enhance': Object.freeze({ 30: 5, 60: 10, 120: 20 }),
  'botgo-vsr-fast': Object.freeze({ 30: 1, 60: 2, 120: 3 }),
});

// Rates above use a 720p output as their baseline. The vendor prices output
// resolution rather than input resolution; input size only limits valid targets.
export const VSR_OUTPUT_RESOLUTION_MULTIPLIERS = Object.freeze({
  '720p': 1,
  '1080p': 2,
  '2k': 4,
  '4k': 8,
});

export const isVideoEnhanceModelKey = (modelKey) => Object.hasOwn(VSR_MODEL_RATES, String(modelKey || '').trim().toLowerCase());

const VSR_SIZES = {
  adaptive: { '720p': 'adaptive_720p', '1080p': 'adaptive_1080p', '2K': 'adaptive_2k', '4K': 'adaptive_4k' },
};

const getVsrOutputResolution = (resolutionLabel, resolution) => {
  const value = String(resolutionLabel || resolution || '').toLowerCase();
  const label = /(?:^|_)\s*(720p|1080p|2k|4k)$/.exec(value)?.[1];
  if (label) return label;
  const dimensions = parseResolution(value);
  if (dimensions.width && dimensions.height) {
    const shortEdge = Math.min(dimensions.width, dimensions.height);
    if (shortEdge >= 2160) return '4k';
    if (shortEdge >= 1440) return '2k';
    if (shortEdge >= 1080) return '1080p';
  }
  return '720p';
};

const getVsrCreditCost = ({ modelKey, duration, fps = 30, resolution, resolutionLabel } = {}) => {
  const seconds = Number(duration);
  if (!Number.isFinite(seconds) || seconds <= 0) return 0;
  const rate = VSR_MODEL_RATES[modelKey]?.[Number(fps)];
  if (!Number.isFinite(rate)) return 0;
  const output = getVsrOutputResolution(resolutionLabel, resolution);
  return Math.round(Math.round(seconds) * rate * VSR_OUTPUT_RESOLUTION_MULTIPLIERS[output]);
};

// Autumn Fantasia prices are the effective rates charged in credit mode.
// Keep list rates here too so the generator and pricing page can share one source.
export const SEEDANCE_AUTUMN_PRICES_PER_SECOND = {
  'seedance-2.5': { '720p': { standard: 60, realPerson: 80 }, '1080p': { standard: 100, realPerson: 120 } },
  'seedance-2.0': { '720p': { standard: 50, realPerson: 60 }, '1080p': { standard: 70, realPerson: 90 }, '4K': { standard: 150, realPerson: 180 } },
  'seedance-2.0-fast': { '720p': { standard: 30, realPerson: 40 } },
  'seedance-2.0-mini': { '720p': { standard: 10, realPerson: 15 } },
};

// VIP models use native generation. Keep their two privacy-tier rates in one
// place so generator estimates, preflight, and the pricing page cannot drift.
export const SEEDANCE_VIP_PRICES_PER_SECOND = {
  'seedance-2.5-vip': {
    '480p': { standard: 60, realPerson: 78 },
    '720p': { standard: 130, realPerson: 170 },
    '1080p': { standard: 320, realPerson: 410 },
  },
  'seedance-2.0-vip': {
    '480p': { standard: 40, realPerson: 50 },
    '720p': { standard: 88, realPerson: 110 },
    '1080p': { standard: 208, realPerson: 270 },
    '4K': { standard: 416, realPerson: 540 },
  },
  'seedance-2.0-fast-vip': {
    '480p': { standard: 24, realPerson: 30 },
    '720p': { standard: 50, realPerson: 65 },
  },
  'seedance-2.0-mini-vip': {
    '480p': { standard: 8, realPerson: 10 },
    '720p': { standard: 20, realPerson: 26 },
  },
};

const getSeedanceVipCreditCost = ({
  modelKey, resolution, duration, inputVideoDuration, privacyUnrestricted = false,
}) => {
  const resolutionLabel = resolveResolutionLabel({ modelKey, size: resolution }) || String(resolution || '');
  const tier = SEEDANCE_VIP_PRICES_PER_SECOND[modelKey]?.[resolutionLabel];
  if (!tier) return 0;
  return getSeedance20CreditCost({
    modelKey,
    outputDuration: duration,
    inputVideoDuration,
    perSecondCost: privacyUnrestricted ? tier.realPerson : tier.standard,
  });
};

export const isSeedanceSrModelKey = (modelKey) => SEEDANCE_SR_MODEL_KEYS.has(String(modelKey || '').trim().toLowerCase());

const getSeedanceAutumnCreditCost = ({ modelKey, resolution, duration, inputVideoDuration, privacyUnrestricted = false, promotionActive, creditDiscountBasisPoints }) => {
  const rates = SEEDANCE_AUTUMN_PRICES_PER_SECOND[modelKey] || {};
  const resolutionLabel = resolveResolutionLabel({ modelKey, size: resolution }) || String(resolution || '');
  const tier = rates[resolutionLabel];
  if (!tier) return 0;
  const listRate = privacyUnrestricted ? tier.realPerson : tier.standard;
  const discount = Number.isInteger(creditDiscountBasisPoints)
    && creditDiscountBasisPoints >= 0 && creditDiscountBasisPoints < 10000
    ? creditDiscountBasisPoints : (promotionActive === true ? 2000 : 0);
  return getSeedance20CreditCost({
    modelKey,
    outputDuration: duration,
    inputVideoDuration,
    perSecondCost: listRate * (1 - discount / 10000),
  });
};

// MiniMax H3 的 V2 API 用 resolution + ratio 两个独立字段，官方未公布每个
// 比例对应的精确像素尺寸。这里使用稳定的内部 size token 参与前端选项和
// Provider 路由，实际渠道 payload 只发送文档定义的 resolution 与 ratio。
const MINIMAX_H3_ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', 'adaptive'];
const buildMiniMaxSizes = (resolutions) => Object.fromEntries(MINIMAX_H3_ASPECT_RATIOS.map((ratio) => [ratio,
  Object.fromEntries(resolutions.map((resolution) => [resolution, `minimax_h3_${resolution.toLowerCase()}_${ratio.replace(':', '_')}`])),
]));
const createMiniMaxModel = (displayName, group, rates, isMax = false) => ({
  type: 'video', displayName, providerModelNames: [displayName], providerGroups: [group],
  generatorDescription: isMax ? 'Fast image-guided video with native audio.' : 'Multimodal video with native audio, frame control, and editing.',
  creditCost: ({ duration, inputVideoDuration, inputImageCount, resolution, resolutionLabel, modelKey } = {}) => getMiniMaxH3CreditCost({
    modelKey: resolveVideoModelKey(modelKey), inputImageCount,
    outputDuration: duration, inputVideoDuration: isMax ? 0 : inputVideoDuration,
    perSecondCost: resolveCreditCostByResolution({ modelKey, resolution, resolutionLabel, costByResolution: rates, fallback: Object.values(rates)[0] }),
  }),
  capabilities: {
    allowedAspectRatios: MINIMAX_H3_ASPECT_RATIOS,
    allowedResolutions: Object.keys(rates), allowedSizesByAspect: buildMiniMaxSizes(Object.keys(rates)),
    allowedDurations: Array.from({ length: isMax ? 11 : 12 }, (_, i) => i + (isMax ? 5 : 4)), defaultDuration: 5,
    allowedInputModes: isMax ? ['text', 'image'] : ['text', 'image', 'video', 'audio'],
    allowedVideoFunctionModes: isMax ? ['general', 'frames', 'reference'] : ['general', 'frames', 'reference', 'editing'],
    maxInputImages: 9, maxInputVideos: isMax ? 0 : 3, maxInputAudios: isMax ? 0 : 3,
    supportsContextIr: true,
  },
});

const HAPPYHORSE_SIZES = {
  '16:9': { '720p': '1280x720', '1080p': '1920x1080' },
  '9:16': { '720p': '720x1280', '1080p': '1080x1920' },
  '1:1':  { '720p': '720x720',  '1080p': '1080x1080' },
  '4:3':  { '720p': '960x720',  '1080p': '1440x1080' },
  '3:4':  { '720p': '720x960',  '1080p': '1080x1440' },
};

// 官方文档：duration 取值为 [3, 15] 之间的整数，默认 5
const HAPPYHORSE_DURATIONS = Array.from({ length: 13 }, (_, i) => i + 3);

const HAPPYHORSE_CREDIT_COST_BY_RESOLUTION = {
  '720p': 48,
  '1080p': 86,
};

const getHappyHorseCreditCost = ({ resolution, duration, modelKey } = {}) => {
  const perSecondCost = resolveCreditCostByResolution({
    modelKey,
    resolution,
    costByResolution: HAPPYHORSE_CREDIT_COST_BY_RESOLUTION,
    fallback: HAPPYHORSE_CREDIT_COST_BY_RESOLUTION['720p'],
  });
  const seconds = Number(duration);
  const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 5;
  return perSecondCost * safeSeconds;
};

const HAPPYHORSE_CAPABILITIES = {
  allowedAspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4'],
  allowedResolutions: ['720p', '1080p'],
  allowedSizesByAspect: HAPPYHORSE_SIZES,
  allowedDurations: HAPPYHORSE_DURATIONS,
  defaultDuration: 5,
  allowedInputModes: ['text', 'image'],
  allowedVideoFunctionModes: ['general', 'reference'],
};

const KLING_V3_SIZES = {
  '16:9': { '720p': '1280x720', '1080p': '1920x1080', '4K': '3840x2160' },
  '9:16': { '720p': '720x1280', '1080p': '1080x1920', '4K': '2160x3840' },
  '1:1': { '720p': '720x720', '1080p': '1080x1080', '4K': '2160x2160' },
};

const KLING_V3_OMNI_SIZES = {
  '16:9': { '720p': '1280x720', '1080p': '1920x1080', '4K': '3840x2160' },
  '9:16': { '720p': '720x1280', '1080p': '1080x1920', '4K': '2160x3840' },
  '1:1': { '720p': '720x720', '1080p': '1080x1080', '4K': '2160x2160' },
};

const KLING_V3_ASPECT_RATIOS = ['16:9', '9:16', '1:1'];
const KLING_V3_STANDARD_RESOLUTIONS = ['720p', '1080p', '4K'];
const KLING_V3_OMNI_RESOLUTIONS = ['720p', '1080p', '4K'];
const KLING_V3_DURATIONS = Array.from({ length: 13 }, (_, i) => i + 3);

const VEO_SIZES = {
  '9:16': { '720p': '720x1280', '1080p': '1080x1920', '4K': '2160x3840' },
  '16:9': { '720p': '1280x720', '1080p': '1920x1080', '4K': '3840x2160' },
};

const VEO_FAST_SPECIAL_SIZES = {
  '9:16': { '720p': '720x1280', '1080p': '1080x1920' },
  '16:9': { '720p': '1280x720', '1080p': '1920x1080' },
};

const VEO31_GEMINI_VIP_DURATIONS = [4, 6, 8];
const VEO31_GEMINI_EIGHT_SECOND_DURATIONS = [8];
const VEO31_GEMINI_VIP_DURATION_BY_RESOLUTION = {
  '1080p': VEO31_GEMINI_EIGHT_SECOND_DURATIONS,
  '4K': VEO31_GEMINI_EIGHT_SECOND_DURATIONS,
};
const VEO31_GEMINI_LITE_VIP_DURATION_BY_RESOLUTION = {
  '1080p': VEO31_GEMINI_EIGHT_SECOND_DURATIONS,
};
const VEO31_GEMINI_VIP_DURATION_BY_FUNCTION_MODE = {
  reference: VEO31_GEMINI_EIGHT_SECOND_DURATIONS,
  extend: VEO31_GEMINI_EIGHT_SECOND_DURATIONS,
};

const GROK_IMAGINE_SIZES = {
  '2:3': { '480p': '480x720', '720p': '720x1080' },
  '1:1': { '480p': '480x480', '720p': '720x720' },
  '3:2': { '480p': '720x480', '720p': '1080x720' },
  '9:16': { '480p': '480x854', '720p': '720x1280' },
  '16:9': { '480p': '854x480', '720p': '1280x720' },
};

const GROK_IMAGINE_SPECIAL_SIZES = {
  '1:1': { '720p': '720x720' },
  '2:3': { '720p': '720x1080' },
  '3:2': { '720p': '1080x720' },
};

const GROK_IMAGINE_VIDEO_15_SIZES = {
  '1:1': { '480p': '480x480', '720p': '720x720' },
  '9:16': { '480p': '480x854', '720p': '720x1280' },
  '16:9': { '480p': '854x480', '720p': '1280x720' },
  '4:3': { '480p': '640x480', '720p': '960x720' },
  '3:4': { '480p': '480x640', '720p': '720x960' },
  '3:2': { '480p': '720x480', '720p': '1080x720' },
  '2:3': { '480p': '480x720', '720p': '720x1080' },
};

const GROK_IMAGINE_VIDEO_15_SPECIAL_SIZES = Object.fromEntries(
  Object.entries(GROK_IMAGINE_VIDEO_15_SIZES).map(([aspectRatio, sizes]) => [
    aspectRatio,
    { '720p': sizes['720p'] },
  ]),
);

const GEMINI_OMNI_11_SIZES = {
  '16:9': { '360p': '640x360', '720p': '1280x720', '1080p': '1920x1080', '4K': '3840x2160' },
  '9:16': { '360p': '360x640', '720p': '720x1280', '1080p': '1080x1920', '4K': '2160x3840' },
};

const GEMINI_OMNI_FLASH_VIP_SIZES = {
  '16:9': { '720p': '1280x720' },
  '9:16': { '720p': '720x1280' },
};

const GROK_IMAGINE_VIDEO_VIP_SIZES = {
  '16:9': { '480p': '854x480', '720p': '1280x720' },
  '9:16': { '480p': '480x854', '720p': '720x1280' },
};

const GROK_IMAGINE_VIDEO_15_VIP_SIZES = {
  '1:1': { '480p': '480x480', '720p': '720x720', '1080p': '1080x1080' },
  '9:16': { '480p': '480x854', '720p': '720x1280', '1080p': '1080x1920' },
  '16:9': { '480p': '854x480', '720p': '1280x720', '1080p': '1920x1080' },
  '4:3': { '480p': '640x480', '720p': '960x720', '1080p': '1440x1080' },
  '3:4': { '480p': '480x640', '720p': '720x960', '1080p': '1080x1440' },
  '3:2': { '480p': '720x480', '720p': '1080x720', '1080p': '1620x1080' },
  '2:3': { '480p': '480x720', '720p': '720x1080', '1080p': '1080x1620' },
};

const GROK_IMAGINE_DURATIONS = Array.from({ length: 25 }, (_, index) => index + 6);
const GROK_IMAGINE_SPECIAL_DURATIONS = [6, 10];
const GROK_IMAGINE_VIDEO_15_DURATIONS = Array.from({ length: 15 }, (_, index) => index + 1);
const GROK_IMAGINE_VIDEO_15_SPECIAL_DURATIONS = [15];
const GEMINI_OMNI_FLASH_VIP_DURATIONS = Array.from({ length: 8 }, (_, index) => index + 3);
const GROK_IMAGINE_VIDEO_VIP_DURATIONS = Array.from({ length: 9 }, (_, index) => index + 2);

const BOTGO_VIDEO_2_SIZES = {
  '16:9': { '480p': '854x480', '720p': '1280x720', '1080p': '1920x1080' },
  '9:16': { '480p': '480x854', '720p': '720x1280', '1080p': '1080x1920' },
  '1:1': { '480p': '480x480', '720p': '720x720', '1080p': '1080x1080' },
  '4:3': { '480p': '640x480', '720p': '960x720', '1080p': '1440x1080' },
  '3:4': { '480p': '480x640', '720p': '720x960', '1080p': '1080x1440' },
};
const BOTGO_VIDEO_2_ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4'];
const BOTGO_VIDEO_2_RESOLUTIONS = ['480p', '720p', '1080p'];
const BOTGO_VIDEO_2_DURATIONS = Array.from({ length: 10 }, (_, index) => index + 6);
const BOTGO_VIDEO_2_DURATION_BY_RESOLUTION = {
  '1080p': [6, 7, 8, 9, 10],
};

const BOTGO_VIDEO_25_FLASH_SIZES = {
  '21:9': { '720p': '1680x720' },
  '16:9': { '720p': '1280x704' },
  '4:3': { '720p': '960x720' },
  '1:1': { '720p': '720x720' },
  '3:4': { '720p': '720x960' },
  '9:16': { '720p': '720x1280' },
};
const BOTGO_VIDEO_25_FLASH_ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'];
const BOTGO_VIDEO_25_FLASH_DURATIONS = Array.from({ length: 9 }, (_, index) => index + 4);

const NANO_BANANA_SIZES = {
  '1:1': { '720p': '1024x1024' },
  '2:3': { '720p': '832x1248' },
  '3:2': { '720p': '1248x832' },
  '3:4': { '720p': '864x1184' },
  '4:3': { '720p': '1184x864' },
  '4:5': { '720p': '896x1152' },
  '5:4': { '720p': '1152x896' },
  '9:16': { '720p': '768x1344' },
  '16:9': { '720p': '1344x768' },
  '21:9': { '720p': '1536x672' },
};

const NANO_BANANA_PRO_SIZES = {
  '1:1': { '1K': '1024x1024', '2K': '2048x2048', '4K': '4096x4096' },
  '2:3': { '1K': '848x1264', '2K': '1696x2528', '4K': '3392x5056' },
  '3:2': { '1K': '1264x848', '2K': '2528x1696', '4K': '5056x3392' },
  '3:4': { '1K': '896x1200', '2K': '1792x2400', '4K': '3584x4800' },
  '4:3': { '1K': '1200x896', '2K': '2400x1792', '4K': '4800x3584' },
  '4:5': { '1K': '928x1152', '2K': '1856x2304', '4K': '3712x4608' },
  '5:4': { '1K': '1152x928', '2K': '2304x1856', '4K': '4608x3712' },
  '9:16': { '1K': '768x1376', '2K': '1536x2752', '4K': '3072x5504' },
  '16:9': { '1K': '1376x768', '2K': '2752x1536', '4K': '5504x3072' },
  '21:9': { '1K': '1584x672', '2K': '3168x1344', '4K': '6336x2688' },
};

const SEEDREAM_50_ASPECT_RATIOS = ['1:1', '4:3', '3:4', '16:9', '9:16', '3:2', '2:3', '21:9'];

const SEEDREAM_50_PRO_SIZES = {
  '1:1': { '1K': '1024x1024', '2K': '2048x2048' },
  '4:3': { '1K': '1152x864', '2K': '2368x1776' },
  '3:4': { '1K': '864x1152', '2K': '1776x2368' },
  '16:9': { '1K': '1424x800', '2K': '2816x1584' },
  '9:16': { '1K': '800x1424', '2K': '1584x2816' },
  '3:2': { '1K': '1248x832', '2K': '2496x1664' },
  '2:3': { '1K': '832x1248', '2K': '1664x2496' },
  '21:9': { '1K': '1568x672', '2K': '3136x1344' },
};

const SEEDREAM_50_LITE_SIZES = {
  '1:1': { '2K': '2048x2048', '3K': '3072x3072', '4K': '4096x4096' },
  '4:3': { '2K': '2304x1728', '3K': '3456x2592', '4K': '4704x3520' },
  '3:4': { '2K': '1728x2304', '3K': '2592x3456', '4K': '3520x4704' },
  '16:9': { '2K': '2848x1600', '3K': '4096x2304', '4K': '5504x3040' },
  '9:16': { '2K': '1600x2848', '3K': '2304x4096', '4K': '3040x5504' },
  '3:2': { '2K': '2496x1664', '3K': '3744x2496', '4K': '4992x3328' },
  '2:3': { '2K': '1664x2496', '3K': '2496x3744', '4K': '3328x4992' },
  '21:9': { '2K': '3136x1344', '3K': '4704x2016', '4K': '6240x2656' },
};

const NANO_BANANA_2_SIZES = {
  auto: { '1K': 'auto', '2K': 'auto', '4K': 'auto' },
  '1:1': { '0.5K': '512x512', '1K': '1024x1024', '2K': '2048x2048', '4K': '4096x4096' },
  '1:4': { '0.5K': '256x1024', '1K': '512x2048', '2K': '1024x4096', '4K': '2048x8192' },
  '1:8': { '0.5K': '192x1536', '1K': '384x3072', '2K': '768x6144', '4K': '1536x12288' },
  '2:3': { '0.5K': '424x632', '1K': '848x1264', '2K': '1696x2528', '4K': '3392x5056' },
  '3:2': { '0.5K': '632x424', '1K': '1264x848', '2K': '2528x1696', '4K': '5056x3392' },
  '3:4': { '0.5K': '448x600', '1K': '896x1200', '2K': '1792x2400', '4K': '3584x4800' },
  '4:1': { '0.5K': '1024x256', '1K': '2048x512', '2K': '4096x1024', '4K': '8192x2048' },
  '4:3': { '0.5K': '600x448', '1K': '1200x896', '2K': '2400x1792', '4K': '4800x3584' },
  '4:5': { '0.5K': '464x576', '1K': '928x1152', '2K': '1856x2304', '4K': '3712x4608' },
  '5:4': { '0.5K': '576x464', '1K': '1152x928', '2K': '2304x1856', '4K': '4608x3712' },
  '8:1': { '0.5K': '1536x192', '1K': '3072x384', '2K': '6144x768', '4K': '12288x1536' },
  '9:16': { '0.5K': '384x688', '1K': '768x1376', '2K': '1536x2752', '4K': '3072x5504' },
  '16:9': { '0.5K': '688x384', '1K': '1376x768', '2K': '2752x1536', '4K': '5504x3072' },
  '21:9': { '0.5K': '792x168', '1K': '1584x672', '2K': '3168x1344', '4K': '6336x2688' },
};

const NANO_BANANA_2_ASPECT_RATIOS = [
  'auto',
  '1:1',
  '1:4',
  '1:8',
  '2:3',
  '3:2',
  '3:4',
  '4:1',
  '4:3',
  '4:5',
  '5:4',
  '8:1',
  '9:16',
  '16:9',
  '21:9',
];

const NANO_BANANA_2_LITE_ASPECT_RATIOS = NANO_BANANA_2_ASPECT_RATIOS;

const NANO_BANANA_2_LITE_SIZES = {
  auto: { '1K': 'auto' },
  ...Object.fromEntries(
    Object.entries(NANO_BANANA_2_SIZES).map(([aspectRatio, sizes]) => [
      aspectRatio,
      { '1K': sizes['1K'] },
    ]),
  ),
};

const GPT_IMAGE_2_ASPECT_RATIOS = [
  'auto',
  '1:1',
  '2:3',
  '3:2',
  '3:4',
  '4:3',
  '4:5',
  '5:4',
  '9:16',
  '16:9',
  '1:2',
  '2:1',
  '9:21',
  '21:9',
];

const GPT_IMAGE_2_SIZES = {
  'auto': { '1K': 'auto', '2K': 'auto', '4K': 'auto' },
  '1:1': { '1K': '1024x1024', '2K': '2048x2048', '4K': '2880x2880' },
  '2:3': { '1K': '1024x1536', '2K': '1360x2048', '4K': '1920x2880' },
  '3:2': { '1K': '1536x1024', '2K': '2048x1360', '4K': '2880x1920' },
  '3:4': { '1K': '768x1024', '2K': '1536x2048', '4K': '2160x2880' },
  '4:3': { '1K': '1024x768', '2K': '2048x1536', '4K': '2880x2160' },
  '4:5': { '1K': '1024x1280', '2K': '2048x2560', '4K': '2304x2880' },
  '5:4': { '1K': '1280x1024', '2K': '2560x2048', '4K': '2880x2304' },
  '9:16': { '1K': '864x1536', '2K': '1152x2048', '4K': '2160x3840' },
  '16:9': { '1K': '1536x864', '2K': '2048x1152', '4K': '3840x2160' },
  '1:2': { '1K': '1024x2048', '2K': '1344x2688', '4K': '1920x3840' },
  '2:1': { '1K': '2048x1024', '2K': '2688x1344', '4K': '3840x1920' },
  '9:21': { '1K': '864x2016', '2K': '1152x2688', '4K': '1648x3840' },
  '21:9': { '1K': '2016x864', '2K': '2688x1152', '4K': '3840x1648' },
};

const BOTGO_IMAGE_2_ASPECT_RATIOS = [
  '1:1',
  '2:3',
  '3:2',
  '3:4',
  '4:3',
  '4:5',
  '5:4',
  '9:16',
  '16:9',
  '21:9',
];

const BOTGO_IMAGE_2_SIZES = {
  '1:1': { '1K': '1024x1024', '2K': '2048x2048', '4K': '4096x4096' },
  '2:3': { '1K': '1024x1536', '2K': '1360x2048', '4K': '1920x2880' },
  '3:2': { '1K': '1536x1024', '2K': '2048x1360', '4K': '2880x1920' },
  '3:4': { '1K': '768x1024', '2K': '1536x2048', '4K': '2160x2880' },
  '4:3': { '1K': '1024x768', '2K': '2048x1536', '4K': '2880x2160' },
  '4:5': { '1K': '1024x1280', '2K': '1640x2048', '4K': '2304x2880' },
  '5:4': { '1K': '1280x1024', '2K': '2048x1640', '4K': '2880x2304' },
  '9:16': { '1K': '864x1536', '2K': '1152x2048', '4K': '2160x3840' },
  '16:9': { '1K': '1536x864', '2K': '2048x1152', '4K': '3840x2160' },
  '21:9': { '1K': '2016x864', '2K': '2048x880', '4K': '3840x1648' },
};

const BOTGO_IMAGE_25_ASPECT_RATIOS = ['1:1', '3:4', '4:3', '16:9', '9:16', '2:3', '3:2', '21:9'];
const BOTGO_IMAGE_25_SIZES = {
  '1:1': { '1K': '1024x1024', '2K': '2048x2048', '3K': '3072x3072', '4K': '4096x4096' },
  '3:4': { '1K': '864x1152', '2K': '1728x2304', '3K': '2592x3456', '4K': '3456x4608' },
  '4:3': { '1K': '1152x864', '2K': '2304x1728', '3K': '3456x2592', '4K': '4608x3456' },
  '16:9': { '1K': '1312x736', '2K': '2624x1472', '3K': '3936x2208', '4K': '5248x2944' },
  '9:16': { '1K': '736x1312', '2K': '1472x2624', '3K': '2208x3936', '4K': '2944x5248' },
  '2:3': { '1K': '832x1248', '2K': '1664x2496', '3K': '2496x3744', '4K': '3328x4992' },
  '3:2': { '1K': '1248x832', '2K': '2496x1664', '3K': '3744x2496', '4K': '4992x3328' },
  '21:9': { '1K': '1568x672', '2K': '3136x1344', '3K': '4704x2016', '4K': '6272x2688' },
};

export const MODEL_REGISTRY = {
  'botgo-vsr-enhance': {
    type: 'video',
    displayName: 'BotGo VSR Enhance',
    aliases: ['botgovsrenhance', 'botgo-vsr-enhance'],
    creditCost: getVsrCreditCost,
    generatorDescription: 'Generative video quality enhancement that preserves the source composition.',
    capabilities: {
      allowedAspectRatios: ['adaptive'],
      allowedResolutions: ['720p', '1080p', '2K', '4K'],
      allowedSizesByAspect: VSR_SIZES,
      allowedDurations: [5],
      defaultDuration: 5,
      allowedInputModes: ['video'],
      allowedVideoFunctionModes: ['enhance'],
      maxInputVideos: 1,
    },
  },
  'botgo-vsr-fast': {
    type: 'video',
    displayName: 'BotGo VSR Fast',
    aliases: ['botgovsrfast', 'botgo-vsr-fast'],
    creditCost: getVsrCreditCost,
    generatorDescription: 'Fast video quality enhancement that preserves the source composition.',
    capabilities: {
      allowedAspectRatios: ['adaptive'],
      allowedResolutions: ['720p', '1080p', '2K', '4K'],
      allowedSizesByAspect: VSR_SIZES,
      allowedDurations: [5],
      defaultDuration: 5,
      allowedInputModes: ['video'],
      allowedVideoFunctionModes: ['enhance'],
      maxInputVideos: 1,
    },
  },
  'sora-2': {
    type: 'video',
    displayName: 'Sora 2',
    providerModelNames: ['Sora 2'],
    aliases: [
      'sora2',
      'sora_2',
      'sora-2',
      'sora2vip',
      'sora_2_vip',
      'sora-2-vip',
      'sora2vipstandard',
      'sora_2_vip_standard',
      'sora-2-vip-standard',
      'sora2vip30',
      'sora_2_vip_30',
      'sora-2-vip-30',
    ],
    creditCost: ({ duration } = {}) => {
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 4;
      return safeSeconds * 32;
    },
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p'],
      allowedSizesByAspect: SORA2_SIZES,
      allowedDurations: [4, 8, 12, 16, 20],
      allowedInputModes: ['text', 'image'],
    },
  },
  'sora-2-vip': {
    type: 'video',
    displayName: formatProviderGroupedDisplayName('Sora 2', 'VIP'),
    providerModelNames: ['Sora 2'],
    providerGroups: ['VIP'],
    hiddenInGenerator: true,
    deprecated: true,
    replacementModelKey: 'sora-2',
    aliases: [
      'sora2vip',
      'sora_2_vip',
      'sora-2-vip',
      'sora2vipstandard',
      'sora_2_vip_standard',
      'sora-2-vip-standard',
      'sora2vip30',
      'sora_2_vip_30',
      'sora-2-vip-30',
    ],
    creditCost: ({ duration } = {}) => {
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 4;
      return safeSeconds * 32;
    },
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p'],
      allowedSizesByAspect: SORA2_SIZES,
      allowedDurations: [4, 8, 12, 16, 20],
      allowedInputModes: ['text', 'image'],
    },
  },
  'sora-2-special': {
    type: 'video',
    displayName: 'Sora 2 Special',
    providerModelNames: ['Sora 2'],
    providerGroups: ['Special'],
    hiddenInGenerator: true,
    aliases: [
      'sora2special',
      'sora-2-special',
      'sora_2_special',
      'sora 2 special',
      'lconai-sora-2',
      'lconai_video_fast_sora-2',
    ],
    creditCost: () => 50,
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p'],
      allowedSizesByAspect: SORA2_SIZES,
      allowedDurations: [4, 8, 12],
      defaultDuration: 4,
      allowedInputModes: ['text', 'image'],
      allowedVideoFunctionModes: ['general'],
    },
  },
  'seedance-1.5-pro': {
    type: 'video',
    displayName: 'Seedance 1.5 Pro',
    providerModelNames: ['Seedance 1.5 Pro'],
    aliases: [
      'seedance',
      'seedance15pro',
      'seedance-1.5-pro',
      'seedance-15-pro',
      'bytedance/seedance-1.5-pro',
    ],
    creditCost: ({ resolution, duration, modelKey }) => {
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution: {
          '480p': 8,
          '720p': 16,
          '1080p': 32,
        },
        fallback: 16,
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 4;
      return perSecondCost * safeSeconds;
    },
    capabilities: {
      allowedAspectRatios: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'],
      allowedResolutions: ['480p', '720p', '1080p'],
      allowedSizesByAspect: SEEDANCE_15_SIZES,
      allowedDurations: [4, 5, 6, 7, 8, 9, 10, 11, 12],
      allowedInputModes: ['text', 'image'],
      allowedVideoFunctionModes: ['general', 'frames'],
      supportsSeed: true,
      supportsWebSearch: true,
    },
  },
  'seedance-2.0-mini': {
    type: 'video',
    displayName: 'Seedance 2.0 Mini',
    providerModelNames: ['Seedance 2.0 Mini'],
    providerGroups: ['Default'],
    aliases: [
      'seedance20mini',
      'seedance-20-mini',
      'seedance-2.0-mini',
      'seedance2mini',
      'seedance-2-mini',
      'doubao-seedance-2-0-mini',
      'bytedance/seedance-2-mini',
    ],
    creditCost: getSeedanceAutumnCreditCost,
    capabilities: {
      allowedAspectRatios: SEEDANCE_20_VIP_ASPECT_RATIOS,
      allowedResolutions: ['720p'],
      allowedSizesByAspect: SEEDANCE_20_MINI_SIZES,
      allowedDurations: SEEDANCE_20_DURATIONS,
      defaultDuration: 5,
      allowedInputModes: ['text', 'image', 'video', 'audio'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference', 'editing', 'extend'],
      supportsSeed: true,
      supportsWebSearch: true,
    },
  },
  'seedance-2.0-fast': {
    type: 'video',
    displayName: 'Seedance 2.0 Fast',
    providerModelNames: ['Seedance 2.0 Fast'],
    providerGroups: ['Default'],
    aliases: [
      'seedance20fast',
      'seedance-20-fast',
      'seedance-2.0-fast',
      'seedance2fast',
      'seedance-2-fast',
    ],
    creditCost: getSeedanceAutumnCreditCost,
    capabilities: {
      allowedAspectRatios: SEEDANCE_20_VIP_ASPECT_RATIOS,
      allowedResolutions: ['720p'],
      allowedSizesByAspect: SEEDANCE_20_FAST_VIP_SIZES,
      allowedDurations: SEEDANCE_20_DURATIONS,
      defaultDuration: 5,
      allowedInputModes: ['text', 'image', 'video', 'audio'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference', 'editing', 'extend'],
      supportsSeed: true,
      supportsWebSearch: true,
    },
  },
  'seedance-2.0-fast-special': {
    type: 'video',
    displayName: formatProviderGroupedDisplayName('Seedance 2.0 Fast', 'Special'),
    providerModelNames: ['Seedance 2.0 Fast'],
    providerGroups: ['Special'],
    hiddenInGenerator: true,
    deprecated: true,
    aliases: [
      'seedance20fastspecial',
      'seedance-20-fast-special',
      'seedance-2.0-fast-special',
      'seedance2fastspecial',
      'seedance-2-fast-special',
    ],
    generatorDescription: 'Limited-time special-price model. Get it before it is gone.',
    creditCost: () => 300,
    capabilities: {
      allowedAspectRatios: SEEDANCE_20_ASPECT_RATIOS,
      allowedResolutions: SEEDANCE_20_FAST_SPECIAL_RESOLUTIONS,
      allowedSizesByAspect: SEEDANCE_20_FAST_SPECIAL_SIZES,
      allowedDurations: SEEDANCE_20_FAST_SPECIAL_DURATIONS,
      defaultDuration: 15,
      allowedInputModes: ['text', 'image'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference'],
    },
  },
  'seedance-2.0': {
    type: 'video',
    displayName: 'Seedance 2.0',
    providerModelNames: ['Seedance 2.0'],
    providerGroups: ['Default'],
    aliases: [
      'seedance20',
      'seedance-20',
      'seedance-2.0',
      'seedance2',
      'seedance-2',
    ],
    creditCost: getSeedanceAutumnCreditCost,
    capabilities: {
      allowedAspectRatios: SEEDANCE_20_VIP_ASPECT_RATIOS,
      allowedResolutions: ['720p', '1080p', '4K'],
      allowedSizesByAspect: SEEDANCE_20_SIZES,
      allowedDurations: SEEDANCE_20_DURATIONS,
      defaultDuration: 5,
      allowedInputModes: ['text', 'image', 'video', 'audio'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference', 'editing', 'extend'],
      supportsSeed: true,
      supportsWebSearch: true,
    },
  },
  'seedance-2.5': {
    type: 'video',
    displayName: 'Seedance 2.5',
    providerModelNames: ['Seedance 2.5'],
    providerGroups: ['Default'],
    aliases: [
      'seedance25',
      'seedance-25',
      'seedance-2.5',
      'seedance2.5',
      'seedance-2-5',
      'bytedance/seedance-2.5',
      'bytedance/seedance-2-5',
      'doubao-seedance-2-5-260628',
    ],
    generatorDescription: '4–30s multimodal video with up to 50 references, native audio, editing, and extension.',
    creditCost: getSeedanceAutumnCreditCost,
    capabilities: {
      allowedAspectRatios: SEEDANCE_20_VIP_ASPECT_RATIOS,
      allowedResolutions: ['720p', '1080p'],
      allowedSizesByAspect: SEEDANCE_25_SIZES,
      allowedDurations: SEEDANCE_25_DURATIONS,
      defaultDuration: 5,
      allowedInputModes: ['text', 'image', 'video', 'audio'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference', 'editing', 'extend'],
      supportsWebSearch: true,
    },
  },
  'seedance-2.0-fast-vip': {
    type: 'video',
    displayName: 'Seedance 2.0 Fast VIP',
    providerModelNames: ['Seedance 2.0 Fast'],
    providerGroups: ['VIP'],
    aliases: [
      'seedance20fastvip',
      'seedance-20-fast-vip',
      'seedance-2.0-fast-vip',
      'seedance2fastvip',
      'legacy-seedance-2.0-fast-vip',
    ],
    creditCost: getSeedanceVipCreditCost,
    capabilities: {
      allowedAspectRatios: SEEDANCE_20_VIP_ASPECT_RATIOS,
      allowedResolutions: SEEDANCE_20_FAST_VIP_RESOLUTIONS,
      allowedSizesByAspect: SEEDANCE_20_FAST_VIP_SIZES,
      allowedDurations: SEEDANCE_20_DURATIONS,
      defaultDuration: 5,
      allowedInputModes: ['text', 'image', 'video', 'audio'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference', 'editing', 'extend'],
      supportsSeed: true,
      supportsWebSearch: true,
    },
  },
  'seedance-2.0-vip': {
    type: 'video',
    displayName: 'Seedance 2.0 VIP',
    providerModelNames: ['Seedance 2.0'],
    providerGroups: ['VIP'],
    aliases: [
      'seedance20vip',
      'seedance-20-vip',
      'seedance-2.0-vip',
      'seedance2vip',
      'legacy-seedance-2.0-vip',
    ],
    creditCost: getSeedanceVipCreditCost,
    capabilities: {
      allowedAspectRatios: SEEDANCE_20_VIP_ASPECT_RATIOS,
      allowedResolutions: SEEDANCE_20_RESOLUTIONS,
      allowedSizesByAspect: SEEDANCE_20_SIZES,
      allowedDurations: SEEDANCE_20_DURATIONS,
      defaultDuration: 5,
      allowedInputModes: ['text', 'image', 'video', 'audio'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference', 'editing', 'extend'],
      supportsSeed: true,
      supportsWebSearch: true,
    },
  },
  'seedance-2.0-mini-vip': {
    type: 'video',
    displayName: 'Seedance 2.0 Mini VIP',
    providerModelNames: ['Seedance 2.0 Mini'],
    providerGroups: ['VIP'],
    aliases: ['seedance20minivip', 'seedance-2.0-mini-vip', 'legacy-seedance-2.0-mini-vip'],
    creditCost: getSeedanceVipCreditCost,
    capabilities: {
      allowedAspectRatios: SEEDANCE_20_VIP_ASPECT_RATIOS,
      allowedResolutions: SEEDANCE_20_MINI_RESOLUTIONS,
      allowedSizesByAspect: SEEDANCE_20_MINI_SIZES,
      allowedDurations: SEEDANCE_20_DURATIONS,
      defaultDuration: 5,
      allowedInputModes: ['text', 'image', 'video', 'audio'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference', 'editing', 'extend'],
      supportsSeed: true,
      supportsWebSearch: true,
    },
  },
  'seedance-2.5-vip': {
    type: 'video',
    displayName: 'Seedance 2.5 VIP',
    providerModelNames: ['Seedance 2.5'],
    providerGroups: ['VIP'],
    aliases: ['seedance25vip', 'seedance-2.5-vip', 'legacy-seedance-2.5-vip'],
    generatorDescription: 'Native 4–30s multimodal video with up to 50 references, audio, editing, and extension.',
    creditCost: getSeedanceVipCreditCost,
    capabilities: {
      allowedAspectRatios: SEEDANCE_20_VIP_ASPECT_RATIOS,
      allowedResolutions: ['480p', '720p', '1080p'],
      allowedSizesByAspect: SEEDANCE_25_SIZES,
      allowedDurations: SEEDANCE_25_DURATIONS,
      defaultDuration: 5,
      allowedInputModes: ['text', 'image', 'video', 'audio'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference', 'editing', 'extend'],
      supportsWebSearch: true,
    },
  },
  'minimax-h3-special': createMiniMaxModel('MiniMax H3 Special', 'Special', { '768p': 6, '2K': 12 }),
  'minimax-h3': {
    ...createMiniMaxModel('MiniMax H3', 'Default', { '768p': 16, '2K': 26 }),
    aliases: ['minimaxh3', 'minimax_h3', 'minimax h3'],
  },
  'minimax-h3-vip': createMiniMaxModel('MiniMax H3 VIP', 'VIP', { '768p': 30, '2K': 50 }),
  'minimax-h3-max': createMiniMaxModel('MiniMax H3 Max', 'Default', { '480p': 20, '768p': 30 }, true),
  'sora-2-pro': {
    type: 'video',
    displayName: 'Sora 2 Pro',
    providerModelNames: ['Sora 2 Pro'],
    aliases: ['sora2pro', 'sora_2_pro', 'sora-2-pro'],
    creditCost: ({ resolution, duration, modelKey }) => {
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 4;
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution: {
          '720p': 96,
          '1024p': 160,
          '1080p': 224,
        },
        fallback: 96,
      });
      return safeSeconds * perSecondCost;
    },
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1024p', '1080p'],
      allowedSizesByAspect: SORA2_PRO_SIZES,
      allowedDurations: [4, 8, 12, 16, 20],
      allowedInputModes: ['text', 'image'],
    },
  },
  'veo-3.1-fast': {
    type: 'video',
    displayName: 'Veo 3.1 Fast',
    providerModelNames: ['Veo 3.1 Fast'],
    providerGroups: ['Default'],
    providerModelNamesByFunctionMode: {
      reference: ['Veo 3.1 Fast'],
    },
    aliases: ['veo31fast', 'veo3.1fast', 'veo_3_1_fast', 'veo-3.1-fast'],
    creditCost: ({ resolution, modelKey }) => resolveCreditCostByResolution({
      modelKey,
      resolution,
      costByResolution: {
        '720p': 60,
        '1080p': 75,
        '4k': 96,
      },
      fallback: 60,
    }),
    generatorDescription: 'Fast Veo workflow with 720p, 1080p, and 4K output.',
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p', '4K'],
      allowedSizesByAspect: VEO_SIZES,
      allowedDurations: [8],
      allowedInputModes: ['text', 'image', 'video'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference', 'extend'],
    },
  },
  'veo-3.1-fast-special': {
    type: 'video',
    displayName: formatProviderGroupedDisplayName('Veo 3.1 Fast', 'Special'),
    providerModelNames: ['Veo 3.1 Fast'],
    providerGroups: ['Special'],
    aliases: [
      'veo31fastspecial',
      'veo3.1fastspecial',
      'veo_3_1_fast_special',
      'veo-3.1-fast-special',
    ],
    creditCost: ({ resolution, modelKey }) => resolveCreditCostByResolution({
      modelKey,
      resolution,
      costByResolution: {
        '720p': 30,
        '1080p': 30,
      },
      fallback: 30,
    }),
    generatorDescription: 'Limited-time special-price model. Get it before it is gone.',
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p'],
      allowedSizesByAspect: VEO_FAST_SPECIAL_SIZES,
      allowedDurations: [8],
      allowedInputModes: ['text', 'image'],
      allowedVideoFunctionModes: ['general', 'frames'],
    },
  },
  'veo-3.1-fast-lite': {
    type: 'video',
    displayName: 'Veo 3.1 Lite',
    providerModelNames: ['Veo 3.1 Lite'],
    providerGroups: ['Default'],
    providerModelNamesByFunctionMode: {
      reference: ['Veo 3.1 Lite'],
    },
    aliases: [
      'veo31fastlite',
      'veo3.1fastlite',
      'veo_3_1_fast_lite',
      'veo-3.1-fast-lite',
    ],
    creditCost: ({ resolution, modelKey }) => resolveCreditCostByResolution({
      modelKey,
      resolution,
      costByResolution: {
        '720p': 30,
        '1080p': 45,
        '4k': 80,
      },
      fallback: 30,
    }),
    generatorDescription: 'Lower-cost Veo fast lane with 720p, 1080p, and 4K output.',
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p', '4K'],
      allowedSizesByAspect: VEO_SIZES,
      allowedDurations: [8],
      allowedInputModes: ['text', 'image'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference'],
    },
  },
  'veo-3.1': {
    type: 'video',
    displayName: 'Veo 3.1',
    providerModelNames: ['Veo 3.1'],
    providerGroups: ['Default'],
    aliases: ['veo31', 'veo3.1', 'veo_3_1', 'veo-3.1'],
    creditCost: ({ resolution, modelKey }) => resolveCreditCostByResolution({
      modelKey,
      resolution,
      costByResolution: {
        '720p': 240,
        '1080p': 260,
        '4k': 560,
      },
      fallback: 240,
    }),
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p', '4K'],
      allowedSizesByAspect: VEO_SIZES,
      allowedDurations: [8],
      allowedInputModes: ['text', 'image', 'video'],
      allowedVideoFunctionModes: ['general', 'frames', 'extend'],
    },
  },
  'veo-3.1-lite-vip': {
    type: 'video',
    displayName: formatProviderGroupedDisplayName('Veo 3.1 Lite', 'VIP'),
    providerModelNames: ['Veo 3.1 Lite'],
    providerGroups: ['VIP'],
    aliases: [
      'veo31litevip',
      'veo3.1litevip',
      'veo_3_1_lite_vip',
      'veo-3.1-lite-vip',
      'veo-3.1-lite-official',
    ],
    creditCost: ({ resolution, duration, modelKey }) => {
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution: {
          '720p': 20,
          '1080p': 32,
        },
        fallback: 20,
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 8;
      return perSecondCost * safeSeconds;
    },
    generatorDescription: 'Official Gemini Lite video generation',
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p'],
      allowedSizesByAspect: VEO_FAST_SPECIAL_SIZES,
      allowedDurations: VEO31_GEMINI_VIP_DURATIONS,
      allowedDurationsByResolution: VEO31_GEMINI_LITE_VIP_DURATION_BY_RESOLUTION,
      allowedInputModes: ['text', 'image'],
      allowedVideoFunctionModes: ['general', 'frames'],
    },
  },
  'veo-3.1-fast-vip': {
    type: 'video',
    displayName: formatProviderGroupedDisplayName('Veo 3.1 Fast', 'VIP'),
    providerModelNames: ['Veo 3.1 Fast'],
    providerGroups: ['VIP'],
    aliases: [
      'veo31fastvip',
      'veo3.1fastvip',
      'veo_3_1_fast_vip',
      'veo-3.1-fast-vip',
      'veo-3.1-fast-official',
    ],
    creditCost: ({ resolution, duration, modelKey }) => {
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution: {
          '720p': 40,
          '1080p': 48,
          '4k': 120,
        },
        fallback: 40,
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 8;
      return perSecondCost * safeSeconds;
    },
    generatorDescription: 'Fast official Gemini video generation',
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p', '4K'],
      allowedSizesByAspect: VEO_SIZES,
      allowedDurations: VEO31_GEMINI_VIP_DURATIONS,
      allowedDurationsByResolution: VEO31_GEMINI_VIP_DURATION_BY_RESOLUTION,
      allowedDurationsByFunctionMode: VEO31_GEMINI_VIP_DURATION_BY_FUNCTION_MODE,
      allowedInputModes: ['text', 'image'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference'],
    },
  },
  'veo-3.1-vip': {
    type: 'video',
    displayName: formatProviderGroupedDisplayName('Veo 3.1', 'VIP'),
    providerModelNames: ['Veo 3.1'],
    providerGroups: ['VIP'],
    aliases: [
      'veo31vip',
      'veo3.1vip',
      'veo_3_1_vip',
      'veo-3.1-vip',
      'veo-3.1-official',
    ],
    creditCost: ({ resolution, duration, modelKey }) => {
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution: {
          '720p': 160,
          '1080p': 160,
          '4k': 240,
        },
        fallback: 160,
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 8;
      return perSecondCost * safeSeconds;
    },
    generatorDescription: 'Official Gemini video with references',
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p', '4K'],
      allowedSizesByAspect: VEO_SIZES,
      allowedDurations: VEO31_GEMINI_VIP_DURATIONS,
      allowedDurationsByResolution: VEO31_GEMINI_VIP_DURATION_BY_RESOLUTION,
      allowedDurationsByFunctionMode: VEO31_GEMINI_VIP_DURATION_BY_FUNCTION_MODE,
      allowedInputModes: ['text', 'image'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference'],
    },
  },
  'veo-3.1-lite-extend': {
    type: 'video',
    displayName: 'Veo 3.1 Lite Extend',
    providerModelNames: ['Veo 3.1 Lite Extend'],
    providerGroups: ['Default'],
    aliases: [
      'veo31liteextend',
      'veo3.1liteextend',
      'veo_3_1_lite_extend',
      'veo-3.1-lite-extend',
    ],
    creditCost: () => 30,
    generatorDescription: 'Extend Veo videos with prompts',
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p', '4K'],
      allowedSizesByAspect: VEO_SIZES,
      allowedDurations: [8],
      allowedInputModes: ['video'],
      allowedVideoFunctionModes: ['extend'],
    },
  },
  'veo-3.1-fast-extend': {
    type: 'video',
    displayName: 'Veo 3.1 Fast Extend',
    providerModelNames: ['Veo 3.1 Fast Extend'],
    providerGroups: ['Default'],
    aliases: [
      'veo31fastextend',
      'veo3.1fastextend',
      'veo_3_1_fast_extend',
      'veo-3.1-fast-extend',
    ],
    creditCost: () => 60,
    generatorDescription: 'Extend Veo videos with prompts',
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p', '4K'],
      allowedSizesByAspect: VEO_SIZES,
      allowedDurations: [8],
      allowedInputModes: ['video'],
      allowedVideoFunctionModes: ['extend'],
    },
  },
  'veo-3.1-extend': {
    type: 'video',
    displayName: 'Veo 3.1 Extend',
    providerModelNames: ['Veo 3.1 Extend'],
    providerGroups: ['Default'],
    aliases: [
      'veo31extend',
      'veo3.1extend',
      'veo_3_1_extend',
      'veo-3.1-extend',
    ],
    creditCost: () => 450,
    generatorDescription: 'Extend Veo videos with prompts',
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p', '4K'],
      allowedSizesByAspect: VEO_SIZES,
      allowedDurations: [8],
      allowedInputModes: ['video'],
      allowedVideoFunctionModes: ['extend'],
    },
  },
  'veo-3.1-lite-extend-vip': {
    type: 'video',
    displayName: formatProviderGroupedDisplayName('Veo 3.1 Lite Extend', 'VIP'),
    providerModelNames: ['Veo 3.1 Lite Extend'],
    providerGroups: ['VIP'],
    aliases: [
      'veo31liteextendvip',
      'veo3.1liteextendvip',
      'veo_3_1_lite_extend_vip',
      'veo-3.1-lite-extend-vip',
      'veo-3.1-lite-extend-official',
    ],
    creditCost: ({ resolution, duration, modelKey }) => {
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution: {
          '720p': 20,
          '1080p': 32,
        },
        fallback: 20,
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 8;
      return perSecondCost * safeSeconds;
    },
    generatorDescription: 'Official model extends uploaded videos with prompts',
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p'],
      allowedSizesByAspect: VEO_FAST_SPECIAL_SIZES,
      allowedDurations: [8],
      allowedInputModes: ['video'],
      allowedVideoFunctionModes: ['extend'],
    },
  },
  'veo-3.1-fast-extend-vip': {
    type: 'video',
    displayName: formatProviderGroupedDisplayName('Veo 3.1 Fast Extend', 'VIP'),
    providerModelNames: ['Veo 3.1 Fast Extend'],
    providerGroups: ['VIP'],
    aliases: [
      'veo31fastextendvip',
      'veo3.1fastextendvip',
      'veo_3_1_fast_extend_vip',
      'veo-3.1-fast-extend-vip',
      'veo-3.1-fast-extend-official',
    ],
    creditCost: ({ resolution, duration, modelKey }) => {
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution: {
          '720p': 40,
          '1080p': 48,
          '4k': 120,
        },
        fallback: 40,
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 8;
      return perSecondCost * safeSeconds;
    },
    generatorDescription: 'Official model extends uploaded videos with prompts',
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p', '4K'],
      allowedSizesByAspect: VEO_SIZES,
      allowedDurations: [8],
      allowedInputModes: ['video'],
      allowedVideoFunctionModes: ['extend'],
    },
  },
  'veo-3.1-extend-vip': {
    type: 'video',
    displayName: formatProviderGroupedDisplayName('Veo 3.1 Extend', 'VIP'),
    providerModelNames: ['Veo 3.1 Extend'],
    providerGroups: ['VIP'],
    aliases: [
      'veo31extendvip',
      'veo3.1extendvip',
      'veo_3_1_extend_vip',
      'veo-3.1-extend-vip',
      'veo-3.1-extend-official',
    ],
    creditCost: ({ resolution, duration, modelKey }) => {
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution: {
          '720p': 160,
          '1080p': 160,
          '4k': 240,
        },
        fallback: 160,
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 8;
      return perSecondCost * safeSeconds;
    },
    generatorDescription: 'Official model extends uploaded videos with prompts',
    capabilities: {
      allowedAspectRatios: ['9:16', '16:9'],
      allowedResolutions: ['720p', '1080p', '4K'],
      allowedSizesByAspect: VEO_SIZES,
      allowedDurations: [8],
      allowedInputModes: ['video'],
      allowedVideoFunctionModes: ['extend'],
    },
  },
  'wan-3.0': {
    type: 'video',
    displayName: 'Wan 3.0',
    providerModelNames: ['Wan 3.0'],
    providerGroups: ['Default'],
    aliases: ['wan3.0-video'],
    generatorDescription: 'Native audio and multimodal references, up to 30 seconds.',
    creditCost: (options) => getWanVideoCreditCost({ ...options, modelKey: 'wan-3.0' }),
    capabilities: WAN_VIDEO_CAPABILITIES,
  },
  'wan-3.0-prime': {
    type: 'video',
    displayName: 'Wan 3.0 Prime',
    providerModelNames: ['Wan 3.0 Prime'],
    providerGroups: ['Default'],
    aliases: ['wan3.0-video-prime'],
    generatorDescription: 'Native audio and multimodal references, up to 30 seconds.',
    creditCost: (options) => getWanVideoCreditCost({ ...options, modelKey: 'wan-3.0-prime' }),
    capabilities: WAN_VIDEO_CAPABILITIES,
  },
  'gemini-omni-1.1-flash': {
    type: 'video',
    displayName: 'Gemini Omni 1.1 Flash',
    providerModelNames: ['Gemini Omni 1.1 Flash'],
    providerGroups: ['Default'],
    aliases: ['gemini-omni-1.1-flash', 'gemini-omni-1-1-flash'],
    generatorDescription: 'Create 360p drafts, upscale to 4K, and edit videos with reference inputs.',
    creditCost: (params) => getGeminiOmniCreditCost({
      ...params,
      resolution: params.resolutionLabel || resolveResolutionLabel({ modelKey: params.modelKey, size: params.resolution }),
    }),
    capabilities: {
      allowedAspectRatios: ['16:9', '9:16'],
      allowedResolutions: ['360p', '720p', '1080p', '4K'],
      allowedSizesByAspect: GEMINI_OMNI_11_SIZES,
      allowedDurations: [4, 6, 8, 10],
      defaultDuration: 6,
      allowedInputModes: ['text', 'image', 'video'],
      allowedVideoFunctionModes: ['general', 'reference', 'editing'],
    },
  },
  'gemini-omni-1.1-flash-vip': {
    type: 'video',
    displayName: 'Gemini Omni 1.1 Flash VIP',
    providerModelNames: ['Gemini Omni 1.1 Flash'],
    providerGroups: ['VIP'],
    aliases: ['gemini-omni-1.1-flash-vip', 'gemini-omni-1-1-flash-vip'],
    generatorDescription: 'Official Google video generation with first/last frames, editing, and extension.',
    creditCost: (params) => getGeminiOmniCreditCost({
      ...params,
      resolution: params.resolutionLabel || resolveResolutionLabel({ modelKey: params.modelKey, size: params.resolution }),
    }),
    capabilities: {
      allowedAspectRatios: ['16:9', '9:16'],
      allowedResolutions: ['360p', '720p', '1080p', '4K'],
      allowedSizesByAspect: GEMINI_OMNI_11_SIZES,
      allowedDurations: GEMINI_OMNI_FLASH_VIP_DURATIONS,
      defaultDuration: 6,
      allowedInputModes: ['text', 'image', 'video'],
      allowedVideoFunctionModes: ['general', 'reference', 'frames', 'editing', 'extend'],
    },
  },
  'gemini-omni': {
    type: 'video',
    displayName: 'Gemini Omni Flash',
    providerModelNames: ['Gemini Omni Flash', 'Omni Flash'],
    providerGroups: ['Default'],
    providerModelNamesByFunctionMode: {
      general: ['Gemini Omni Flash', 'Omni Flash', 'Omni Flash Ext'],
      reference: ['Gemini Omni Flash', 'Omni Flash Components', 'Omni Flash Ext Components'],
      editing: ['Gemini Omni Flash', 'Omni Flash Ext'],
    },
    aliases: [
      'geminiomni',
      'gemini-omni',
      'google-gemini-omni',
      'omniflash',
      'omni-flash',
      'omni flash',
      'geminiomniflash',
      'gemini-omni-flash',
      'google-gemini-omni-flash',
      'omni flash',
    ],
    generatorDescription: 'Google video workflow with text, image, reference, and natural-language editing control.',
    creditCost: (params) => getGeminiOmniCreditCost({
      ...params,
      resolution: params.resolutionLabel || resolveResolutionLabel({ modelKey: params.modelKey, size: params.resolution }),
    }),
    capabilities: {
      allowedAspectRatios: ['16:9', '9:16'],
      allowedResolutions: ['720p', '1080p', '4K'],
      allowedSizesByAspect: VEO_SIZES,
      allowedDurations: [4, 6, 8, 10],
      defaultDuration: 6,
      allowedInputModes: ['text', 'image', 'video'],
      allowedVideoFunctionModes: ['general', 'reference', 'editing'],
    },
  },
  'gemini-omni-vip': {
    type: 'video',
    displayName: formatProviderGroupedDisplayName('Gemini Omni Flash', 'VIP'),
    providerModelNames: ['Gemini Omni Flash'],
    providerGroups: ['VIP'],
    aliases: [
      'geminiomnivip',
      'gemini-omni-vip',
      'geminiomniflashvip',
      'gemini-omni-flash-vip',
      'gemini-omni-flash-preview',
      'gemini-omni-flash-preview-vip',
    ],
    generatorDescription: 'Official Gemini Omni Flash preview channel for 720p generation and natural-language video edits.',
    creditCost: (params) => getGeminiOmniCreditCost({ ...params, resolution: '720p' }),
    capabilities: {
      allowedAspectRatios: ['16:9', '9:16'],
      allowedResolutions: ['720p'],
      allowedSizesByAspect: GEMINI_OMNI_FLASH_VIP_SIZES,
      allowedDurations: GEMINI_OMNI_FLASH_VIP_DURATIONS,
      defaultDuration: 6,
      allowedInputModes: ['text', 'image', 'video'],
      allowedVideoFunctionModes: ['general', 'reference', 'editing'],
    },
  },
  'botgo-video-2.5-flash': {
    type: 'video',
    displayName: 'BotGo Video 2.5 Flash',
    providerModelNames: ['BotGo Video 2.5 Flash'],
    providerGroups: ['Default'],
    aliases: [
      'botgovideo25flash',
      'botgo-video-2.5-flash',
      'botgo video 2.5 flash',
      'agnes-video-2.5-flash',
      'agnes video 2.5 flash',
    ],
    generatorDescription: 'Fast 720p video model for text, first/last frame, and image-reference workflows.',
    creditCost: () => 10,
    capabilities: {
      allowedAspectRatios: BOTGO_VIDEO_25_FLASH_ASPECT_RATIOS,
      allowedResolutions: ['720p'],
      allowedSizesByAspect: BOTGO_VIDEO_25_FLASH_SIZES,
      allowedDurations: BOTGO_VIDEO_25_FLASH_DURATIONS,
      defaultDuration: 5,
      allowedInputModes: ['text', 'image'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference'],
    },
  },
  'botgo-video-2.5': {
    type: 'video',
    requiresPaidPlan: true,
    displayName: 'BotGo Video 2.5',
    providerModelNames: ['BotGo Video 2.5'],
    providerGroups: ['Default'],
    aliases: ['botgovideo25', 'botgo-video-2.5', 'botgo video 2.5', 'agnes-video-2.5', 'agnes video 2.5'],
    generatorDescription: 'Text, keyframe, and multimodal reference video generation up to 2K.',
    creditCost: getBotgoVideo25CreditCost,
    capabilities: BOTGO_VIDEO_25_CAPABILITIES,
  },
  'botgo-video-2': {
    type: 'video',
    retired: true,
    displayName: 'BotGo Video 2',
    providerModelNames: ['BotGo Video 2'],
    providerGroups: ['Default'],
    aliases: [
      'botgovideo2',
      'botgo-video-2',
      'botgo video 2',
      'agnes-video-v2.0',
      'agnes video v2.0',
      'agnes-video-2.0',
      'agnes video 2.0',
    ],
    generatorDescription: 'Fast BotGo video model with text, image, frame, and reference workflows.',
    creditCost: () => 8,
    capabilities: {
      allowedAspectRatios: BOTGO_VIDEO_2_ASPECT_RATIOS,
      allowedResolutions: BOTGO_VIDEO_2_RESOLUTIONS,
      allowedSizesByAspect: BOTGO_VIDEO_2_SIZES,
      allowedDurations: BOTGO_VIDEO_2_DURATIONS,
      allowedDurationsByResolution: BOTGO_VIDEO_2_DURATION_BY_RESOLUTION,
      defaultDuration: 6,
      allowedInputModes: ['text', 'image'],
      allowedVideoFunctionModes: ['general', 'frames', 'reference'],
    },
  },
  'happyhorse-1.1': {
    type: 'video',
    displayName: 'HappyHorse 1.1',
    providerModelNames: ['HappyHorse 1.1'],
    providerGroups: ['Default'],
    aliases: [
      'happyhorse11',
      'happyhorse1.1',
      'happy-horse-1.1',
      'happyhorse-1.1',
      'happy-horse-11',
      'happyhorse-1.1-t2v',
      'happyhorse-1.1-i2v',
      'happyhorse-1.1-r2v',
    ],
    generatorDescription: 'Upgraded HappyHorse model with native audio and stronger reference consistency.',
    creditCost: getHappyHorseCreditCost,
    capabilities: HAPPYHORSE_CAPABILITIES,
  },
  'happyhorse-1.0': {
    type: 'video',
    displayName: 'HappyHorse 1.0',
    providerModelNames: ['HappyHorse 1.0'],
    providerGroups: ['Default'],
    aliases: [
      'happyhorse10',
      'happyhorse1.0',
      'happy-horse-1.0',
      'happyhorse-1.0',
      'happy-horse-10',
      'happyhorse-1.0-t2v',
      'happyhorse-1.0-i2v',
      'happyhorse-1.0-r2v',
    ],
    generatorDescription: 'Public arena standout with strong realism and motion.',
    creditCost: getHappyHorseCreditCost,
    capabilities: HAPPYHORSE_CAPABILITIES,
  },
  'happyhorse-1.0-video-edit': {
    type: 'video',
    displayName: 'HappyHorse 1.0 Video Edit',
    providerModelNames: ['HappyHorse 1.0 Video Edit'],
    providerGroups: ['Default'],
    aliases: [
      'happyhorse10videoedit',
      'happyhorse-1.0-video-edit',
      'happy-horse-1.0-video-edit',
      'happy horse 1.0 video edit',
    ],
    generatorDescription: 'Dedicated HappyHorse video editing model for natural-language changes to uploaded video clips.',
    creditCost: getHappyHorseCreditCost,
    capabilities: {
      ...HAPPYHORSE_CAPABILITIES,
      allowedInputModes: ['text', 'video'],
      allowedVideoFunctionModes: ['editing'],
    },
  },
  'kling-v4': {
    type: 'video',
    displayName: 'Kling 4.0',
    providerModelNames: ['Kling 4.0'],
    providerGroups: ['Default'],
    requiresPaidPlan: true,
    aliases: ['kling-4.0', 'kling4', 'kling 4.0', 'kling-v4', 'kling-4.0/reference-to-video', 'kling-4.0/keyframe-to-video'],
    creditCost: (params = {}) => getKlingV4CreditCost({
      ...params,
      resolution: params.resolutionLabel || resolveResolutionLabel({ modelKey: 'kling-v4', size: params.resolution }) || params.resolution || '720p',
    }),
    capabilities: KLING_V4_CAPABILITIES,
  },
  'kling-v3': {
    type: 'video',
    displayName: 'Kling V3',
    providerModelNames: ['Kling V3'],
    aliases: [
      'kling-v3',
      'klingv3',
      'kling3',
      'kling 3.0',
      'kling-3.0',
      'kling30',
      'kling-3-0',
      'kling-3.0/video',
      'kling-3-0-video',
    ],
    creditCost: ({ resolution, duration, modelKey, generateAudio } = {}) => {
      const costByResolution = generateAudio
        ? KLING_V3_CREDIT_COSTS.standard.audio
        : KLING_V3_CREDIT_COSTS.standard.default;
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution,
        fallback: costByResolution['720p'],
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 5;
      return perSecondCost * safeSeconds;
    },
    capabilities: {
      allowedAspectRatios: KLING_V3_ASPECT_RATIOS,
      allowedResolutions: KLING_V3_STANDARD_RESOLUTIONS,
      allowedSizesByAspect: KLING_V3_SIZES,
      allowedDurations: KLING_V3_DURATIONS,
      defaultDuration: 5,
      allowedInputModes: ['text', 'image'],
      allowedVideoFunctionModes: ['general', 'frames'],
    },
  },
  'kling-v3-omni': {
    type: 'video',
    displayName: 'Kling V3 Omni',
    providerModelNames: ['Kling V3 Omni'],
    aliases: [
      'kling-v3-omni',
      'klingv3omni',
      'kling 3 omni',
      'kling-3-omni',
      'kling 3.0 omni',
      'kling-3.0-omni',
      'kling video 3 omni',
    ],
    creditCost: ({ resolution, duration, modelKey, generateAudio, hasVideoReference } = {}) => {
      const costByResolution = hasVideoReference
        ? KLING_V3_CREDIT_COSTS.omni.videoReference
        : generateAudio
          ? KLING_V3_CREDIT_COSTS.omni.audio
          : KLING_V3_CREDIT_COSTS.omni.default;
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution,
        fallback: costByResolution['720p'],
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 5;
      return perSecondCost * safeSeconds;
    },
    capabilities: {
      allowedAspectRatios: KLING_V3_ASPECT_RATIOS,
      allowedResolutions: KLING_V3_OMNI_RESOLUTIONS,
      allowedSizesByAspect: KLING_V3_OMNI_SIZES,
      allowedDurations: KLING_V3_DURATIONS,
      defaultDuration: 5,
      allowedInputModes: ['text', 'image', 'video'],
      allowedVideoFunctionModes: ['reference', 'editing', 'extend'],
    },
  },
  'grok-imagine': {
    type: 'video',
    displayName: 'Grok Imagine 1.5',
    providerModelNames: ['Grok Imagine 1.5', 'Grok Imagine', 'Grok Imagine 1.0'],
    providerGroups: ['Default'],
    aliases: [
      'grokimagine',
      'grokimagine15',
      'grokimagine10',
      'grok-imagine',
      'grok-imagine-1-5',
      'grok-imagine-1-0',
      'grok-3',
      'grok-video-3',
      'grok-video-3-pro',
      'grok-video-3-10s',
      'grok-video-3-15s',
    ],
    creditCost: ({ resolution, duration, modelKey } = {}) => {
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution: {
          '480p': 3,
          '720p': 6,
        },
        fallback: 3,
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 6;
      return perSecondCost * safeSeconds;
    },
    capabilities: {
      allowedAspectRatios: ['2:3', '1:1', '3:2', '9:16', '16:9'],
      allowedResolutions: ['480p', '720p'],
      allowedSizesByAspect: GROK_IMAGINE_SIZES,
      allowedDurations: GROK_IMAGINE_DURATIONS,
      allowedInputModes: ['text', 'image'],
    },
  },
  'grok-imagine-video-1.5': {
    type: 'video',
    displayName: 'Grok Imagine Video 1.5',
    providerModelNames: ['Grok Imagine Video 1.5'],
    providerGroups: ['Default'],
    aliases: [
      'grokimaginevideo15',
      'grok-imagine-video-1.5',
      'grok-imagine-video-1-5',
      'grok-imagine-video-1-5-preview',
      'grok-imagine-video-1.5-preview',
    ],
    generatorDescription: 'Image-to-video model with native audio and flexible social framing.',
    creditCost: ({ resolution, duration, modelKey } = {}) => {
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution: {
          '480p': 5,
          '720p': 10,
        },
        fallback: 10,
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 8;
      return perSecondCost * safeSeconds;
    },
    capabilities: {
      allowedAspectRatios: ['1:1', '9:16', '16:9', '4:3', '3:4', '3:2', '2:3'],
      allowedResolutions: ['480p', '720p'],
      allowedSizesByAspect: GROK_IMAGINE_VIDEO_15_SIZES,
      allowedDurations: GROK_IMAGINE_VIDEO_15_DURATIONS,
      defaultDuration: 8,
      allowedInputModes: ['image'],
      allowedVideoFunctionModes: ['general'],
    },
  },
  'grok-imagine-video-vip': {
    type: 'video',
    displayName: formatProviderGroupedDisplayName('Grok Imagine Video', 'VIP'),
    providerModelNames: ['Grok Imagine Video'],
    providerGroups: ['VIP'],
    aliases: [
      'grokimaginevideovip',
      'grok-imagine-video-vip',
      'grok-imagine-video',
      'grok-imagine-video-official',
    ],
    generatorDescription: 'Official xAI Grok Imagine Video channel for text/image generation, reference workflows, editing, and extension.',
    creditCost: ({ resolution, duration, modelKey } = {}) => {
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution: {
          '480p': 24,
          '720p': 32,
        },
        fallback: 32,
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 6;
      return perSecondCost * safeSeconds;
    },
    capabilities: {
      allowedAspectRatios: ['16:9', '9:16'],
      allowedResolutions: ['480p', '720p'],
      allowedSizesByAspect: GROK_IMAGINE_VIDEO_VIP_SIZES,
      allowedDurations: GROK_IMAGINE_VIDEO_VIP_DURATIONS,
      defaultDuration: 6,
      allowedInputModes: ['text', 'image', 'video'],
      allowedVideoFunctionModes: ['general', 'reference', 'editing', 'extend'],
    },
  },
  'grok-imagine-video-1.5-vip': {
    type: 'video',
    displayName: formatProviderGroupedDisplayName('Grok Imagine Video 1.5', 'VIP'),
    providerModelNames: ['Grok Imagine Video 1.5'],
    providerGroups: ['VIP'],
    aliases: [
      'grokimaginevideo15vip',
      'grok-imagine-video-1.5-vip',
      'grok-imagine-video-1-5-vip',
      'grok-imagine-video-1.5-official',
    ],
    generatorDescription: 'Official xAI image-to-video channel with 480p, 720p, and 1080p output.',
    creditCost: ({ resolution, duration, modelKey } = {}) => {
      const perSecondCost = resolveCreditCostByResolution({
        modelKey,
        resolution,
        costByResolution: {
          '480p': 32,
          '720p': 56,
          '1080p': 100,
        },
        fallback: 56,
      });
      const seconds = Number(duration);
      const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : 8;
      return perSecondCost * safeSeconds;
    },
    capabilities: {
      allowedAspectRatios: ['1:1', '9:16', '16:9', '4:3', '3:4', '3:2', '2:3'],
      allowedResolutions: ['480p', '720p', '1080p'],
      allowedSizesByAspect: GROK_IMAGINE_VIDEO_15_VIP_SIZES,
      allowedDurations: GROK_IMAGINE_VIDEO_15_DURATIONS,
      defaultDuration: 8,
      allowedInputModes: ['image'],
      allowedVideoFunctionModes: ['general'],
    },
  },
  'grok-imagine-special': {
    type: 'video',
    displayName: 'Grok Imagine Special',
    providerModelNames: ['Grok Imagine', 'Grok Imagine 1.0'],
    providerGroups: ['Special'],
    aliases: [
      'grok-imagine-special',
      'grok imagine special',
      'grok-imagine-1-0-special',
      'grok-video-3-special',
      'grok-video-3-pro-special',
      'grok-video-3-10s-special',
    ],
    creditCost: () => 20,
    capabilities: {
      allowedAspectRatios: ['1:1', '2:3', '3:2'],
      allowedResolutions: ['720p'],
      allowedSizesByAspect: GROK_IMAGINE_SPECIAL_SIZES,
      allowedDurations: GROK_IMAGINE_SPECIAL_DURATIONS,
      allowedInputModes: ['text', 'image'],
    },
  },
  'grok-imagine-video-1.5-special': {
    type: 'video',
    displayName: 'Grok Imagine Video 1.5 Special',
    providerModelNames: ['Grok Imagine Video 1.5'],
    providerGroups: ['Special'],
    hiddenInGenerator: true,
    aliases: [
      'grokimaginevideo15special',
      'grok-imagine-video-1.5-special',
      'grok-imagine-video-1-5-special',
      'grok-imagine-video-1.5-preview-special',
      'grok-imagine-video-1-5-preview-special',
      'grok-imagine-video-1.5-special-preview',
      'grok-imagine-video-1-5-special-preview',
    ],
    generatorDescription: 'Limited-time special-price Grok Imagine Video 1.5 channel for 720p 15-second clips.',
    creditCost: () => 25,
    capabilities: {
      allowedAspectRatios: ['1:1', '9:16', '16:9', '4:3', '3:4', '3:2', '2:3'],
      allowedResolutions: ['720p'],
      allowedSizesByAspect: GROK_IMAGINE_VIDEO_15_SPECIAL_SIZES,
      allowedDurations: GROK_IMAGINE_VIDEO_15_SPECIAL_DURATIONS,
      defaultDuration: 15,
      allowedInputModes: ['image'],
      allowedVideoFunctionModes: ['general'],
    },
  },
  'nano-banana': {
    type: 'image',
    requiresPaidPlan: true,
    displayName: 'Nano Banana',
    providerModelNames: ['Nano Banana'],
    aliases: ['nanobanana', 'nano-banana'],
    creditCost: () => 4,
    capabilities: {
      allowedAspectRatios: [
        '1:1',
        '2:3',
        '3:2',
        '3:4',
        '4:3',
        '4:5',
        '5:4',
        '9:16',
        '16:9',
        '21:9',
      ],
      allowedResolutions: ['720p'],
      allowedSizesByAspect: NANO_BANANA_SIZES,
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 3,
    },
  },
  'nano-banana-2': {
    type: 'image',
    displayName: 'Nano Banana 2',
    providerModelNames: ['Nano Banana 2'],
    aliases: [
      'nanobanana2',
      'nano-banana-2',
      'nana-banana-2',
      'gemini-3.1-flash-image-preview',
      'gemini-3.1-flash-image-preview-2k',
      'gemini-3.1-flash-image-preview-4k',
    ],
    creditCost: ({ resolution, resolutionLabel, modelKey }) => resolveCreditCostByResolution({
      modelKey,
      resolution,
      resolutionLabel,
      costByResolution: {
        '1k': 15,
        '2k': 15,
        '4k': 20,
      },
      fallback: 15,
    }),
    capabilities: {
      allowedAspectRatios: NANO_BANANA_2_ASPECT_RATIOS,
      allowedResolutions: ['1K', '2K', '4K'],
      allowedSizesByAspect: NANO_BANANA_2_SIZES,
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 14,
    },
  },
  'nano-banana-2.1': {
    type: 'image',
    requiresPaidPlan: true,
    displayName: 'Nano Banana 2.1',
    providerModelNames: ['Nano Banana 2.1'],
    providerGroups: ['Default'],
    aliases: ['nanobanana21', 'nano-banana-2.1', 'nano-banana-2-1', 'gemini-nano-banana-2.1', 'gemini-nano-banana-2.1-ext'],
    creditCost: ({ resolution, resolutionLabel, modelKey }) => resolveCreditCostByResolution({
      modelKey, resolution, resolutionLabel,
      costByResolution: NANO_BANANA_21_CREDITS,
      fallback: 5,
    }),
    capabilities: NANO_BANANA_21_CAPABILITIES,
  },
  'nano-banana-2-lite': {
    type: 'image',
    requiresPaidPlan: true,
    displayName: 'Nano Banana 2 Lite',
    providerModelNames: ['Nano Banana 2 Lite'],
    providerGroups: ['Default'],
    aliases: [
      'nanobanana2lite',
      'nano-banana-2-lite',
      'nano banana 2 lite',
      'nano banana lite',
      'gemini-3.1-flash-lite-image',
      'gemini 3.1 flash lite image',
    ],
    generatorDescription: 'Fast Image Generation and editing',
    creditCost: () => 6,
    capabilities: {
      allowedAspectRatios: NANO_BANANA_2_LITE_ASPECT_RATIOS,
      allowedResolutions: ['1K'],
      allowedSizesByAspect: NANO_BANANA_2_LITE_SIZES,
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 10,
    },
  },
  'nano-banana-pro': {
    type: 'image',
    displayName: 'Nano Banana Pro',
    providerModelNames: ['Nano Banana Pro'],
    aliases: [
      'nanobananapro',
      'nano-banana-pro',
    ],
    creditCost: ({ resolution, resolutionLabel, modelKey }) => resolveCreditCostByResolution({
      modelKey,
      resolution,
      resolutionLabel,
      costByResolution: {
        '1k': 25,
        '2k': 25,
        '4k': 50,
      },
      fallback: 25,
    }),
    capabilities: {
      allowedAspectRatios: [
        '1:1',
        '2:3',
        '3:2',
        '3:4',
        '4:3',
        '4:5',
        '5:4',
        '9:16',
        '16:9',
        '21:9',
      ],
      allowedResolutions: ['1K', '2K', '4K'],
      allowedSizesByAspect: NANO_BANANA_PRO_SIZES,
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 14,
    },
  },
  'seedream-5.0-pro': {
    type: 'image',
    requiresPaidPlan: true,
    displayName: 'Seedream 5.0 Pro',
    providerModelNames: ['Seedream 5.0 Pro'],
    providerGroups: ['Default'],
    aliases: [
      'seedream50pro',
      'seedream-5.0-pro',
      'seedream-5-0-pro',
      'seedream 5.0 pro',
      'doubao-seedream-5-0-pro',
      'doubao seedream 5.0 pro',
      'doubao-seedream-5-0-pro-260628',
    ],
    generatorDescription: 'High-quality image generation and reference editing.',
    creditCost: ({ resolution, resolutionLabel, modelKey }) => resolveCreditCostByResolution({
      modelKey, resolution, resolutionLabel,
      costByResolution: { '1K': 20, '2K': 40 },
      fallback: 40,
    }),
    capabilities: {
      allowedAspectRatios: SEEDREAM_50_ASPECT_RATIOS,
      allowedResolutions: ['1K', '2K'],
      allowedSizesByAspect: SEEDREAM_50_PRO_SIZES,
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 10,
    },
  },
  'seedream-5.0-lite': {
    type: 'image',
    requiresPaidPlan: true,
    displayName: 'Seedream 5.0 Lite',
    providerModelNames: ['Seedream 5.0 Lite'],
    providerGroups: ['Default'],
    aliases: [
      'seedream50lite',
      'seedream-5.0-lite',
      'seedream-5-0-lite',
      'seedream 5.0 lite',
      'doubao-seedream-5-0',
      'doubao seedream 5.0 lite',
      'doubao-seedream-5-0-260128',
    ],
    generatorDescription: 'Fast high-resolution image generation and editing.',
    creditCost: () => 15,
    capabilities: {
      allowedAspectRatios: SEEDREAM_50_ASPECT_RATIOS,
      allowedResolutions: ['2K', '3K', '4K'],
      allowedSizesByAspect: SEEDREAM_50_LITE_SIZES,
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      supportsWebSearch: true,
      maxInputImages: 14,
    },
  },
  'botgo-image-2.5-flash': {
    type: 'image',
    displayName: 'BotGo Image 2.5 Flash',
    providerModelNames: ['BotGo Image 2.5 Flash'],
    providerGroups: ['Default'],
    aliases: [
      'botgoimage25',
      'botgo-image-2.5',
      'botgo image 2.5',
      'botgoimage25flash',
      'botgo-image-2.5-flash',
      'botgo image 2.5 flash',
      'agnes-image-2.5-flash',
      'agnes image 2.5 flash',
    ],
    generatorDescription: 'Latest BotGo image model for detailed generation, editing, and multi-image composition.',
    creditCost: () => 4,
    capabilities: {
      allowedAspectRatios: BOTGO_IMAGE_25_ASPECT_RATIOS,
      allowedResolutions: ['1K', '2K', '3K', '4K'],
      allowedSizesByAspect: BOTGO_IMAGE_25_SIZES,
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 3,
    },
  },
  'botgo-image-2': {
    type: 'image',
    displayName: 'BotGo Image 2',
    providerModelNames: ['BotGo Image 2'],
    providerGroups: ['Default'],
    aliases: [
      'botgoimage2',
      'botgo-image-2',
      'botgo image 2',
      'agnes-image-2.1-flash',
      'agnes image 2.1 flash',
      'agnes-image-21-flash',
      'agnes image 21 flash',
    ],
    generatorDescription: 'Fast BotGo image generation and single-reference editing.',
    creditCost: () => 2,
    capabilities: {
      allowedAspectRatios: BOTGO_IMAGE_2_ASPECT_RATIOS,
      allowedResolutions: ['1K', '2K', '4K'],
      allowedSizesByAspect: BOTGO_IMAGE_2_SIZES,
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 1,
    },
  },
  'gpt-image-2.5-special': {
    type: 'image',
    displayName: formatProviderGroupedDisplayName('GPT Image 2.5', 'Special'),
    providerModelNames: ['GPT Image 2.5'],
    providerGroups: ['Special'],
    creditCost: () => 2,
    capabilities: {
      allowedAspectRatios: Object.keys(GPT_IMAGE_25_SIZES),
      allowedResolutions: ['1K'],
      allowedSizesByAspect: Object.fromEntries(Object.keys(GPT_IMAGE_25_SIZES).map((ratio) => [ratio, { '1K': GPT_IMAGE_25_SIZES[ratio]['1K'] }])),
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 16,
    },
  },
  'gpt-image-2.5-flare': {
    type: 'image',
    requiresPaidPlan: true,
    displayName: 'GPT Image 2.5 Flare',
    providerModelNames: ['GPT Image 2.5'],
    providerGroups: ['Default'],
    creditCost: ({ resolution, resolutionLabel, modelKey }) => resolveCreditCostByResolution({ modelKey, resolution, resolutionLabel, costByResolution: GPT_IMAGE_25_DEFAULT_CREDITS, fallback: 4 }),
    capabilities: {
      allowedAspectRatios: GPT_IMAGE_25_EXT_RATIOS,
      allowedResolutions: ['1K', '2K', '4K'],
      allowedSizesByAspect: Object.fromEntries(GPT_IMAGE_25_EXT_RATIOS.map((ratio) => [ratio, GPT_IMAGE_25_SIZES[ratio]])),
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 16,
    },
  },
  'gpt-image-2.5-sunburst': {
    type: 'image',
    requiresPaidPlan: true,
    displayName: 'GPT Image 2.5 Sunburst',
    providerModelNames: ['GPT Image 2.5'],
    providerGroups: ['Default'],
    creditCost: ({ resolution, resolutionLabel, modelKey }) => resolveCreditCostByResolution({ modelKey, resolution, resolutionLabel, costByResolution: GPT_IMAGE_25_DEFAULT_CREDITS, fallback: 4 }),
    capabilities: {
      allowedAspectRatios: GPT_IMAGE_25_EXT_RATIOS,
      allowedResolutions: ['1K', '2K', '4K'],
      allowedSizesByAspect: Object.fromEntries(GPT_IMAGE_25_EXT_RATIOS.map((ratio) => [ratio, GPT_IMAGE_25_SIZES[ratio]])),
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 16,
    },
  },
  'gpt-image-2.5-flare-vip': {
    type: 'image',
    displayName: 'GPT Image 2.5 Flare VIP',
    providerModelNames: ['GPT Image 2.5 Flare'],
    providerGroups: ['VIP'],
    creditCost: ({ resolution, resolutionLabel, quality, modelKey, inputImageCount = 0 }) => {
      const q = GPT_IMAGE_25_QUALITIES.includes(quality) ? quality : 'low';
      const costByResolution = Object.fromEntries(Object.entries(GPT_IMAGE_25_VIP_CREDITS).map(([r, costs]) => [r.toLowerCase(), costs[q]]));
      const outputCredits = resolveCreditCostByResolution({ modelKey, resolution, resolutionLabel, costByResolution, fallback: costByResolution['1k'] });
      const count = Number(inputImageCount);
      const referenceCount = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
      return outputCredits * (1 + referenceCount * GPT_IMAGE_25_VIP_INPUT_IMAGE_RATE);
    },
    capabilities: {
      allowedAspectRatios: Object.keys(GPT_IMAGE_25_SIZES),
      allowedResolutions: ['1K', '2K', '4K'],
      allowedSizesByAspect: GPT_IMAGE_25_SIZES,
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 16,
      allowedQualities: GPT_IMAGE_25_QUALITIES,
      defaultQuality: 'low',
      allowedModerations: ['auto', 'low'],
      defaultModeration: 'auto',
      allowedBackgrounds: ['transparent', 'opaque', 'auto'],
      defaultBackground: 'auto',
    },
  },
  'gpt-image-2.5-sunburst-vip': {
    type: 'image',
    displayName: 'GPT Image 2.5 Sunburst VIP',
    providerModelNames: ['GPT Image 2.5 Sunburst'],
    providerGroups: ['VIP'],
    creditCost: ({ resolution, resolutionLabel, quality, modelKey, inputImageCount = 0 }) => {
      const q = GPT_IMAGE_25_QUALITIES.includes(quality) ? quality : 'low';
      const costByResolution = Object.fromEntries(Object.entries(GPT_IMAGE_25_VIP_CREDITS).map(([r, costs]) => [r.toLowerCase(), costs[q]]));
      const outputCredits = resolveCreditCostByResolution({ modelKey, resolution, resolutionLabel, costByResolution, fallback: costByResolution['1k'] });
      const count = Number(inputImageCount);
      const referenceCount = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
      return outputCredits * (1 + referenceCount * GPT_IMAGE_25_VIP_INPUT_IMAGE_RATE);
    },
    capabilities: {
      allowedAspectRatios: Object.keys(GPT_IMAGE_25_SIZES),
      allowedResolutions: ['1K', '2K', '4K'],
      allowedSizesByAspect: GPT_IMAGE_25_SIZES,
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 16,
      allowedQualities: GPT_IMAGE_25_QUALITIES,
      defaultQuality: 'low',
      allowedModerations: ['auto', 'low'],
      defaultModeration: 'auto',
      allowedBackgrounds: ['transparent', 'opaque', 'auto'],
      defaultBackground: 'auto',
    },
  },
  'gpt-image-2-special': {
    type: 'image',
    displayName: formatProviderGroupedDisplayName('GPT Image 2', 'Special'),
    providerModelNames: ['GPT Image 2'],
    providerGroups: ['Special'],
    aliases: [],
    creditCost: () => 2,
    generatorDescription: 'Limited-time special-price model',
    capabilities: {
      allowedAspectRatios: GPT_IMAGE_2_ASPECT_RATIOS,
      allowedResolutions: ['1K'],
      allowedSizesByAspect: Object.fromEntries(Object.entries(GPT_IMAGE_2_SIZES).map(([ratio, sizes]) => [ratio, { '1K': sizes['1K'] }])),
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 16,
    },
  },
  'gpt-image-2': {
    type: 'image',
    requiresPaidPlan: true,
    displayName: 'GPT Image 2',
    providerModelNames: ['GPT Image 2'],
    providerGroups: ['Default'],
    aliases: [
      'gptimage2',
      'gpt-image-2',
      'gpt-image-2-all',
      'gpt_image_2',
      'gpt_image_2_all',
    ],
    creditCost: ({ resolution, resolutionLabel, modelKey }) => resolveCreditCostByResolution({
      modelKey,
      resolution,
      resolutionLabel,
      costByResolution: {
        '1k': 4,
        '2k': 6,
        '4k': 9,
      },
      fallback: 4,
    }),
    generatorDescription: 'OpenAI-native image generation with cleaner text rendering and UI-grade detail',
    capabilities: {
      allowedAspectRatios: GPT_IMAGE_2_ASPECT_RATIOS,
      allowedResolutions: ['1K', '2K', '4K'],
      allowedSizesByAspect: GPT_IMAGE_2_SIZES,
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 16,
    },
  },
  'gpt-image-2-vip': {
    type: 'image',
    displayName: formatProviderGroupedDisplayName('GPT Image 2', 'VIP'),
    providerModelNames: ['GPT Image 2'],
    providerGroups: ['VIP'],
    aliases: [
      'gptimage2vip',
      'gpt-image-2-vip',
      'gpt_image_2_vip',
      'gpt-image-2-official',
      'gpt_image_2_official',
      'gptimage2official',
    ],
    creditCost: ({ resolution, resolutionLabel, quality, modelKey }) => {
      const q = String(quality || 'low').toLowerCase();
      const costByResolution =
        q === 'high'
          ? { '1k': 50, '2k': 100, '4k': 250 }
          : q === 'medium'
            ? { '1k': 16, '2k': 32, '4k': 80 }
            : { '1k': 4, '2k': 8, '4k': 12 }; // low (default)
      return resolveCreditCostByResolution({
        modelKey,
        resolution,
        resolutionLabel,
        costByResolution,
        fallback: costByResolution['1k'],
      });
    },
    capabilities: {
      allowedAspectRatios: GPT_IMAGE_2_ASPECT_RATIOS,
      allowedResolutions: ['1K', '2K', '4K'],
      allowedSizesByAspect: GPT_IMAGE_2_SIZES,
      allowedInputModes: ['text', 'image'],
      allowedImageFunctionModes: ['general', 'editing'],
      maxInputImages: 16,
      allowedQualities: ['low', 'medium', 'high'],
      defaultQuality: 'low',
      allowedModerations: ['auto', 'low'],
      defaultModeration: 'auto',
    },
  },
};

const MODEL_ALIAS_MAP = new Map();

// Canonical keys and display names take precedence over shared upstream names.
// In particular, "GPT Image 2" must not resolve to its Free Special variant.
Object.entries(MODEL_REGISTRY).forEach(([key, config]) => {
  [key, config.displayName].forEach((value) => {
    const normalized = normalizeAlias(value);
    if (normalized && !MODEL_ALIAS_MAP.has(normalized)) MODEL_ALIAS_MAP.set(normalized, key);
  });
});

Object.entries(MODEL_REGISTRY).forEach(([key, config]) => {
  const aliases = [
    key,
    config.displayName,
    ...(config.providerModelNames || []),
    ...Object.values(config.providerModelNamesByFunctionMode || {}).flat(),
    ...(config.aliases || []),
  ];
  aliases.forEach((alias) => {
    const normalized = normalizeAlias(alias);
    if (!normalized) return;
    if (!MODEL_ALIAS_MAP.has(normalized)) {
      MODEL_ALIAS_MAP.set(normalized, key);
    }
  });
});

export const normalizeModelName = normalizeAlias;

export function resolveModelKey(value = '') {
  const normalized = normalizeAlias(value);
  return MODEL_ALIAS_MAP.get(normalized) || null;
}

export function resolveVideoModelKey(value = '') {
  const key = resolveModelKey(value);
  if (!key) return null;
  return MODEL_REGISTRY[key]?.type === 'video' ? key : null;
}

export function resolveImageModelKey(value = '') {
  const key = resolveModelKey(value);
  if (!key) return null;
  return MODEL_REGISTRY[key]?.type === 'image' ? key : null;
}

export function getModelConfig(modelKey) {
  const resolvedKey = MODEL_REGISTRY[modelKey] ? modelKey : resolveModelKey(modelKey || '');
  return resolvedKey ? MODEL_REGISTRY[resolvedKey] || null : null;
}

export function getModelCapabilities(modelKey) {
  return getModelConfig(modelKey)?.capabilities || null;
}

export function getProviderModelNamesForRequest({
  modelKey,
  modelName,
  functionMode = 'general',
} = {}) {
  const resolvedKey = modelKey || resolveModelKey(modelName || '');
  const config = resolvedKey ? getModelConfig(resolvedKey) : null;
  const modeKey = String(functionMode || 'general').trim().toLowerCase();
  const modeSpecificNames = config?.providerModelNamesByFunctionMode?.[modeKey];

  if (Array.isArray(modeSpecificNames) && modeSpecificNames.length > 0) {
    return modeSpecificNames;
  }

  if (Array.isArray(config?.providerModelNames) && config.providerModelNames.length > 0) {
    return config.providerModelNames;
  }

  if (typeof modelName === 'string' && modelName.trim()) {
    return [modelName.trim()];
  }

  return [];
}

export function getProviderGroupsForRequest({
  modelKey,
  modelName,
  functionMode = 'general',
} = {}) {
  const resolvedKey = modelKey || resolveModelKey(modelName || '');
  const config = resolvedKey ? getModelConfig(resolvedKey) : null;
  const modeKey = String(functionMode || 'general').trim().toLowerCase();
  const modeSpecificGroups = config?.providerGroupsByFunctionMode?.[modeKey];

  if (Array.isArray(modeSpecificGroups) && modeSpecificGroups.length > 0) {
    return modeSpecificGroups;
  }

  if (Array.isArray(config?.providerGroups) && config.providerGroups.length > 0) {
    return config.providerGroups;
  }

  if (config) {
    return ['Default'];
  }

  return null;
}

const findSizeInMap = (sizesByAspect, size) => {
  if (!sizesByAspect || !size) return null;
  const normalizedSize = normalizeSize(size);
  for (const [aspect, entries] of Object.entries(sizesByAspect)) {
    if (!entries || typeof entries !== 'object') continue;
    for (const [label, mappedSize] of Object.entries(entries)) {
      if (normalizeSize(mappedSize) === normalizedSize) {
        return { aspect, label };
      }
    }
  }
  return null;
};

const resolveAspectRatioByValue = (width, height) => {
  if (!width || !height) return null;
  const ratio = width / height;
  let best = null;
  let bestDiff = Number.POSITIVE_INFINITY;
  for (const [label, value] of Object.entries(ASPECT_RATIO_VALUES)) {
    const diff = Math.abs(ratio - value);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = label;
    }
  }
  return bestDiff <= 0.08 ? best : null;
};

export function normalizeAspectRatioValue(value = '') {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) return null;
  if (normalizeSize(normalizedValue) === 'auto') {
    return 'auto';
  }

  const embeddedMatch = normalizedValue.match(/(\d+\s*:\s*\d+)/);
  if (embeddedMatch) {
    return normalizeAspectRatioLabel(embeddedMatch[1].replace(/\s+/g, ''));
  }

  const compactValue = normalizedValue.replace(/\s+/g, '');
  for (const label of Object.keys(ASPECT_RATIO_VALUES)) {
    if (normalizeAspectRatioLabel(label) === normalizeAspectRatioLabel(compactValue)) {
      return label;
    }
  }

  return null;
}

export function getAspectOrientation(value = '') {
  const aspectRatio = normalizeAspectRatioValue(value);
  if (!aspectRatio || aspectRatio === 'auto') return null;

  const [width, height] = aspectRatio.split(':').map(Number);
  if (!width || !height) return null;
  return width >= height ? 'landscape' : 'portrait';
}

export function formatAspectRatioForStorage(value = '') {
  const aspectRatio = normalizeAspectRatioValue(value);
  if (!aspectRatio || aspectRatio === 'auto') return null;

  const orientation = getAspectOrientation(aspectRatio);
  const prefix = orientation === 'landscape' ? 'Landscape' : 'Portrait';
  return `${prefix} (${aspectRatio})`;
}

export function resolveAspectRatioLabel({ modelKey, size } = {}) {
  const normalizedSize = normalizeSize(size);
  if (normalizedSize === 'auto') {
    return 'auto';
  }

  const caps = getModelCapabilities(modelKey);
  const match = findSizeInMap(caps?.allowedSizesByAspect, size);
  if (match?.aspect) return normalizeAspectRatioLabel(match.aspect);

  const parts = normalizedSize.split('x');
  if (parts.length === 2) {
    const width = Number(parts[0]);
    const height = Number(parts[1]);
    const derived = resolveAspectRatioByValue(width, height);
    if (derived) return derived;
    return width >= height ? '16:9' : '9:16';
  }
  return null;
}

export function resolveResolutionLabel({ modelKey, size } = {}) {
  const normalizedSize = normalizeSize(size);
  if (normalizedSize === 'auto') {
    return null;
  }

  const caps = getModelCapabilities(modelKey);
  const allowed = (caps?.allowedResolutions || []).map(normalizeResolutionLabel);
  const finalizeResolutionLabel = (label) => {
    if (!label) return null;
    const normalizedLabel = normalizeResolutionLabel(label);
    if (allowed.length && !allowed.includes(normalizedLabel)) {
      return null;
    }
    return label;
  };
  const match = findSizeInMap(caps?.allowedSizesByAspect, size);
  if (match?.label) return finalizeResolutionLabel(match.label);
  const parts = normalizedSize.split('x');
  if (parts.length === 2) {
    const width = Number(parts[0]);
    const height = Number(parts[1]);
    const maxDim = Math.max(width || 0, height || 0);
    if (allowed.includes('4k')) {
      if (maxDim >= 3840) return '4K';
    }
    if (allowed.includes('2k')) {
      if (maxDim >= 2048) return '2K';
    }
    if (allowed.includes('1k')) {
      if (maxDim >= 1024) return '1K';
    }
    if (allowed.includes('0.5k')) {
      if (maxDim >= 512) return '0.5K';
    }
    if (maxDim >= 1920) return finalizeResolutionLabel('1080p');
    if (maxDim >= 1280) return finalizeResolutionLabel('720p');
    if (maxDim >= 854) return finalizeResolutionLabel('480p');
  }
  return null;
}

const normalizeDurationList = (list = []) => {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  return list.reduce((acc, item) => {
    const duration = Number(item);
    if (!Number.isFinite(duration) || duration <= 0 || seen.has(duration)) return acc;
    seen.add(duration);
    acc.push(duration);
    return acc;
  }, []);
};

const intersectDurationLists = (base = [], restriction = []) => {
  const normalizedRestriction = normalizeDurationList(restriction);
  if (!normalizedRestriction.length) return normalizeDurationList(base);
  const normalizedBase = normalizeDurationList(base);
  if (!normalizedBase.length) return normalizedRestriction;
  return normalizedBase.filter((duration) => normalizedRestriction.includes(duration));
};

const resolveDurationRestriction = (map, value, normalizer = normalizeOptionalText) => {
  if (!map || typeof map !== 'object') return null;
  const normalizedValue = normalizer(value || '');
  if (!normalizedValue) return null;
  const match = Object.entries(map).find(
    ([key]) => normalizer(key) === normalizedValue
  );
  return Array.isArray(match?.[1]) ? match[1] : null;
};

export function getAllowedDurationsForVideoRequest({
  modelKey,
  resolutionLabel,
  size,
  functionMode,
} = {}) {
  const caps = getModelCapabilities(modelKey);
  if (!caps) return [];

  let durations = normalizeDurationList(caps.allowedDurations);
  const resolvedResolutionLabel =
    resolutionLabel || (size ? resolveResolutionLabel({ modelKey, size }) : null);

  durations = intersectDurationLists(
    durations,
    resolveDurationRestriction(
      caps.allowedDurationsByResolution,
      resolvedResolutionLabel,
      normalizeResolutionLabel
    )
  );
  durations = intersectDurationLists(
    durations,
    resolveDurationRestriction(
      caps.allowedDurationsByFunctionMode,
      functionMode || 'general',
      (value) => String(value || '').trim().toLowerCase()
    )
  );

  return durations;
}

function resolveCreditCostByResolution({
  modelKey,
  resolution,
  resolutionLabel,
  costByResolution,
  fallback = 0,
} = {}) {
  if (!costByResolution || typeof costByResolution !== 'object') {
    return Number(fallback) || 0;
  }

  const normalizedCostMap = Object.entries(costByResolution).reduce((acc, [key, value]) => {
    const normalizedKey = normalizeResolutionLabel(key);
    acc[normalizedKey] = Number(value) || 0;
    return acc;
  }, {});

  const explicitResolutionLabel = normalizeOptionalText(resolutionLabel);
  const resolvedResolutionLabel =
    explicitResolutionLabel || resolveResolutionLabel({ modelKey, size: resolution });
  const normalizedLabel = normalizeResolutionLabel(resolvedResolutionLabel || resolution);

  if (normalizedLabel && normalizedCostMap.hasOwnProperty(normalizedLabel)) {
    return normalizedCostMap[normalizedLabel];
  }

  if (resolvedResolutionLabel && normalizedCostMap.hasOwnProperty(normalizeResolutionLabel(resolvedResolutionLabel))) {
    return normalizedCostMap[normalizeResolutionLabel(resolvedResolutionLabel)];
  }

  return Number(fallback) || 0;
}

export function getModelCreditCost({
  modelKey,
  resolution,
  resolutionLabel,
  duration,
  quality,
  generateAudio,
  functionMode,
  hasVideoReference,
  inputVideoDuration,
  inputImageCount,
  privacyUnrestricted,
  promotionActive,
  creditDiscountBasisPoints,
  fps,
} = {}) {
  const config = getModelConfig(modelKey);
  if (!config || typeof config.creditCost !== 'function') return 0;
  return Number(config.creditCost({
    resolution,
    resolutionLabel,
    duration,
    quality,
    generateAudio,
    functionMode,
    hasVideoReference,
    inputVideoDuration,
    inputImageCount,
    privacyUnrestricted,
    promotionActive,
    creditDiscountBasisPoints,
    fps,
    modelKey,
  })) || 0;
}

export function getVideoCreditCost({
  model,
  modelKey,
  resolution,
  resolutionLabel,
  duration,
  generateAudio,
  contextIrEnabled,
  functionMode,
  hasVideoReference,
  inputVideoDuration,
  inputImageCount,
  privacyUnrestricted,
  promotionActive,
  creditDiscountBasisPoints,
  fps,
} = {}) {
  const resolvedKey = modelKey || resolveVideoModelKey(model || '');
  const baseCost = getModelCreditCost({
    modelKey: resolvedKey,
    resolution,
    resolutionLabel,
    duration,
    generateAudio,
    functionMode,
    hasVideoReference,
    inputVideoDuration,
    inputImageCount,
    privacyUnrestricted,
    promotionActive,
    creditDiscountBasisPoints,
    fps,
  });
  return Math.round(baseCost + (contextIrEnabled === true && getModelConfig(resolvedKey)?.capabilities?.supportsContextIr ? 1.5 : 0));
}

export function getImageCreditCost({ model, modelKey, resolution, resolutionLabel, quality, functionMode, inputImageCount } = {}) {
  const resolvedKey = modelKey || resolveImageModelKey(model || '');
  return getModelCreditCost({ modelKey: resolvedKey, resolution, resolutionLabel, quality, functionMode, inputImageCount });
}

export function isResolution4k(value = '') {
  return parseResolution(value).is4k;
}
